/**
 * Wanderlaut — main entry point.
 *
 * Architecture:
 *   1. Mount skin (immediate — welcome screen visible instantly)
 *   2. Check auth → show Sign-In or START
 *   3. On START: acquire pool → init guide → start tracking
 *   4. Guide lifecycle: start → pause → resume → stop
 *
 * No blocking operations before the UI is visible.
 * Pool acquire happens lazily on user action, not on page load.
 */

import { AudioGuide, SessionStateManager } from '@arkturian/audio-guide';
import type { AudioGuideConfig, RideMode, TransportMode } from '@arkturian/audio-guide';
import { DefaultSkin } from '@/view/default';
import { ImmersiveSkin } from '@/view/immersive/ImmersiveSkin';
import { isLoggedIn, getUser, startGoogleLogin, handleCallback, isInAppBrowser, refreshToken, devAutoLogin } from '@/auth';
import { createSession, logInteraction, logSong, endSession, attachAudio, attachImage, getNearbyInteraction, triggerReview, findReviewedImage, findImageFast, getRandomCity, GUIDE_API_BASE } from '@/guideApiClient';
import { pushHeard, getHeardTag } from '@/heard';
import { t, getLang, setLang, applyDomTranslations, LANG_LIST } from '@/i18n';
import type { LangCode } from '@/i18n';

// ──────────────────────────────────────────────────────────────
// Constants
// ──────────────────────────────────────────────────────────────

const POOL_NAME = 'guide';
const USER_ID_KEY = 'audio-guide-user-id';
const CLOUD_API = 'https://cloud-api.arkserver.arkturian.com';
const LOG_API = 'https://log-api.arkturian.com/api/v1';
const LOG_SESSION = 'audio-guide';

// ── Age-profile feature ───────────────────────────────────────
// Single toggle to enable/disable: hides the UI badge AND skips sending
// the [AGE:N] tag to the bot. Flip to false to turn the whole experiment
// off without removing any code.
const AGE_FEATURE_ENABLED = true;

// ── Route-Overlay (optional, Evaluierung) ─────────────────────
// Zeigt geplante Route + POIs + Flora/Fauna-Screenpoints auf der Karte,
// per Button an-/abschaltbar. EIN Flag killt das ganze Feature (Button
// erscheint nicht, kein Code-Pfad aktiv). Folgt dem AKTIVEN Track; fester
// Test-Override via ?overlayTrack=N.
const ROUTE_OVERLAY_ENABLED = true;

// — Narration-Korpus-Player (Phase 2, Modus ② Statisch/ArTrack). Toggle-bar,
// self-contained, kein Effekt auf den Live-Guide solange der User ihn nicht
// einschaltet. Button rechts oben. ?corpusTrack=N erzwingt einen Track,
// ?corpusStatic=1 legt den Realtime-Agent still, ?corpusStep=1 zeigt einen
// Step-Button (Desk-Test ohne Laufen).
const NARRATION_CORPUS_ENABLED = true;

// — Dev-Agent-Selector im Welcome-Screen (DEV-only, killbar). Dropdown +
// Frei-Text zur Guide-Session-Wahl ohne ?session= von Hand. Erscheint nur
// auf *-dev-Hosts / localhost / ?dev=1, nie auf Prod. Self-contained.
const DEV_AGENT_SELECTOR = true;

// — Realtime-Voice-Guide (Content #1196): opt-in Live-Sprach-Modus (gpt-4o-mini-
// realtime via WebRTC). Dev-gated Button + DSGVO-Consent → guide.startRealtime
// Session(). Additiv neben Live/Korpus, killbar. Bucht erst bei User-Consent
// gegen den Realtime-Cap.
const REALTIME_VOICE_ENABLED = true;

// — World-Korpus Phase 1 (Content #1169): im generischen Modus (kein aktiver
// Track) generische Narrationen via `world_corpus=true` an guide-api spiegeln
// → wächst zum deterministisch abspielbaren Welt-Korpus (guide-api macht
// Radius-Dedupe). Nur Inhalt (narration_point), keine GPS-Telemetrie (Phase 2,
// geparkt). Killbar.
const WORLD_CORPUS_ENABLED = true;

// ── HEARD-history feature ─────────────────────────────────────
// Device-local time-decay memory of what the bot already told this
// user. Lives in localStorage, gates by 500m radius around the
// current GPS fix, decays in two tiers (HARD 0-2h, SOFT 2-24h, gone
// after 24h). See @/heard for the full contract and content-api
// post 808 for the design rationale.
//
// The earlier server-side /interactions/heard endpoint is bypassed
// — its cross-device global semantics over-restricted famous
// landmarks. Frontend-only is the right layer for this UX.

// ── Onboarding-preferences persistence ────────────────────────
// Remembers the user's last chip selection, transport mode, language
// and content mode across sessions, so the onboarding UI is
// pre-populated on return visits. Stored in a single localStorage JSON
// blob — easy to extend, easy to clear, easy to migrate later to a
// guide-api PATCH /users/me/preferences if cross-device sync becomes
// important. Disable with PREFS_PERSIST_ENABLED=false to test the
// "first-visit" experience.
const PREFS_PERSIST_ENABLED = true;
const PREFS_STORAGE_KEY = 'wanderlaut-prefs';

interface StoredPrefs {
  intents?: string[];
  transport?: string;
  language?: string;
  mode?: string;
  // Last "konkreter Ort" string (Tscheppaschlucht, Salzburg, ...).
  // Persisted so a session-reset (dead-session 240s timeout → reload)
  // doesn't silently fall back to live-GPS — the user picked a specific
  // place and expects to wake up there, not at their current location.
  // Cleared explicitly when the user picks "Hier" or "Überraschung"
  // in the next visit.
  customLocation?: string;
  /** Realtime-Engine als Guide-Default (persistent, Content #1196). Wenn true,
   *  bootet der Guide ohne URL-Param in den Realtime-Modus. Vom 🎙-Toggle gesetzt. */
  realtime?: boolean;
  /** deep_dive (Content #1213/#1214): N progressively deeper layers of the SAME
   *  topic (0 = off, 1-3) + pause (sec) between layers (the "Abspielverhältnis"). */
  deepDive?: { depth: number; layerPauseSec: number };
}

function getStoredPrefs(): StoredPrefs {
  if (!PREFS_PERSIST_ENABLED) return {};
  try {
    const raw = localStorage.getItem(PREFS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object') ? parsed : {};
  } catch {
    return {};
  }
}
function setStoredPrefs(prefs: StoredPrefs): void {
  if (!PREFS_PERSIST_ENABLED) return;
  try {
    const current = getStoredPrefs();
    const merged = { ...current, ...prefs };
    localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify(merged));
  } catch { /* quota / blocked → silently skip */ }
}
/** Realtime ist der Guide-Default, wenn als Pref gesetzt ODER `?realtime=1`
 *  (one-off). So braucht der User keinen URL-Param — einmal per 🎙-Toggle
 *  einschalten, dann bleibt's. */
/** Read the deep_dive config: URL params (?depth=N&layerpause=S) override the stored pref. */
function readDeepDivePrefs(): { depth: number; layerPauseSec: number } {
  const qs = new URLSearchParams(location.search);
  const stored = getStoredPrefs().deepDive;
  const qD = qs.get('depth');
  const qP = qs.get('layerpause');
  const depth = qD !== null ? Number(qD) : stored?.depth ?? 0;
  const pause = qP !== null ? Number(qP) : stored?.layerPauseSec ?? 4;
  return {
    depth: Math.max(0, Math.min(3, Math.floor(Number.isFinite(depth) ? depth : 0))),
    layerPauseSec: Math.max(0, Math.min(30, Number.isFinite(pause) ? pause : 4)),
  };
}

/** Apply the deep_dive config to a guide: depth (sent in gps_update) + player
 *  behavior (deeper layers gated on lingering, configurable inter-layer pause).
 *  One clean path — no hacky per-mode branches; the lib state machine reads it. */
function applyDeepDive(g: AudioGuide | null): void {
  if (!g) return;
  const dd = readDeepDivePrefs();
  g.setDeepDiveDepth(dd.depth);
  g.setPlayerBehavior({
    layerPauseMs: Math.round(dd.layerPauseSec * 1000),
    followupBeatsPending: dd.depth === 0,
  });
}

function isRealtimeMode(): boolean {
  // Realtime ist DEPRIORISIERT (Alex 2026-06-23: Kosten — "Hotline zum Reden"-
  // Use-Case, nicht der Kern). Nur noch explizit per URL opt-in (?realtime=1 /
  // =voice), KEIN persistenter Pref mehr — sonst klebt der Realtime-Modus und
  // versteckt den Agent-Selector. So ist der normale GuideAgent-Pfad +
  // #devAgentSelector wieder der DEFAULT für Alex' Tests.
  const rt = new URLSearchParams(location.search).get('realtime');
  return rt === '1' || rt === 'voice';
}

const BIRTHDATE_STORAGE_KEY = 'wanderlaut-birthdate'; // ISO YYYY-MM-DD

function getStoredBirthdate(): string | null {
  if (!AGE_FEATURE_ENABLED) return null;
  const raw = localStorage.getItem(BIRTHDATE_STORAGE_KEY);
  if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  return raw;
}
function setStoredBirthdate(iso: string | null): void {
  if (iso === null) localStorage.removeItem(BIRTHDATE_STORAGE_KEY);
  else localStorage.setItem(BIRTHDATE_STORAGE_KEY, iso);
}
function computeAge(birthdateIso: string): number | null {
  const [y, m, d] = birthdateIso.split('-').map(n => parseInt(n, 10));
  if (!y || !m || !d) return null;
  const today = new Date();
  let age = today.getFullYear() - y;
  if (today.getMonth() + 1 < m || (today.getMonth() + 1 === m && today.getDate() < d)) age--;
  return (age >= 1 && age <= 120) ? age : null;
}

// ──────────────────────────────────────────────────────────────
// Remote Logging
// ──────────────────────────────────────────────────────────────

window.addEventListener('error', (e) => {
  remoteLog(`UNCAUGHT: ${e.message} at ${e.filename}:${e.lineno}`, 'global', 'Error');
});
window.addEventListener('unhandledrejection', (e) => {
  remoteLog(`UNHANDLED REJECT: ${e.reason instanceof Error ? e.reason.message : String(e.reason)}`, 'global', 'Error');
});

function remoteLog(message: string, source: string, type: 'Log' | 'Warning' | 'Error' = 'Log'): void {
  fetch(`${LOG_API}/sessions/${LOG_SESSION}/logs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ entries: [{ type, message, source }] }),
  }).catch(() => {});
}

// ──────────────────────────────────────────────────────────────
// Pool Management
// ──────────────────────────────────────────────────────────────

function getOrCreateUserId(): string {
  let uid = localStorage.getItem(USER_ID_KEY);
  if (!uid) {
    uid = `guide-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(USER_ID_KEY, uid);
  }
  return uid;
}

async function acquirePool(): Promise<string> {
  const userId = getOrCreateUserId();
  const url = `${CLOUD_API}/api/pools/${POOL_NAME}/acquire`;
  const body = JSON.stringify({ user_id: userId });
  const headers = { 'Content-Type': 'application/json' };

  const MAX_RETRIES = 10;
  const RETRY_DELAY = 3000;

  // Verbose acquire trace so we can correlate three-phones-at-once
  // experiments in log-api: every attempt logs latency + full server
  // response shape (HTTP, action, error, booting/starting hints).
  const traceId = `acq-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
  remoteLog(`Acquire START trace=${traceId} user=${userId.slice(0, 12)}…`, 'pool');

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    const t0 = Date.now();
    try {
      const res = await fetch(url, { method: 'POST', headers, body });
      const elapsed = Date.now() - t0;
      let data: any = {};
      try { data = await res.json(); } catch { data = { _parseError: true }; }

      // Always log the raw response shape so we can reconstruct what
      // happened minute-by-minute on the server side.
      remoteLog(
        `Acquire #${attempt + 1}/${MAX_RETRIES} trace=${traceId} http=${res.status} `
        + `${elapsed}ms action=${data.action || '?'} `
        + `instance=${data.instance || '-'} error="${(data.error || '').slice(0, 100)}" `
        + `booting=${JSON.stringify(data.booting || [])} starting=${data.starting || '-'}`,
        'pool',
        res.ok ? 'Log' : 'Warning',
      );

      if (data.instance) {
        remoteLog(`Acquire DONE trace=${traceId} → ${data.instance} (after ${attempt + 1} attempts)`, 'pool');
        return data.instance;
      }

      const error = data.error || '';
      if (error.includes('no available')) {
        remoteLog(`Acquire FAIL trace=${traceId} pool exhausted`, 'pool', 'Error');
        throw new Error('Pool voll — alle Sessions belegt');
      }

      // Retryable: instance starting or not ready
      updateStatus(
        t('status.botStarting', { attempt: attempt + 1, total: MAX_RETRIES }),
        t('status.botStartingDetail'),
      );
      await sleep(RETRY_DELAY);
    } catch (err) {
      const elapsed = Date.now() - t0;
      const msg = err instanceof Error ? err.message : String(err);
      remoteLog(
        `Acquire #${attempt + 1}/${MAX_RETRIES} trace=${traceId} EXCEPTION after ${elapsed}ms: ${msg.slice(0, 200)}`,
        'pool',
        'Error',
      );
      if (attempt === MAX_RETRIES - 1) throw err;
      await sleep(RETRY_DELAY);
    }
  }
  remoteLog(`Acquire EXHAUSTED trace=${traceId} after ${MAX_RETRIES} attempts`, 'pool', 'Error');
  throw new Error('Pool acquire fehlgeschlagen');
}

function releasePool(sessionName: string): void {
  const url = `${CLOUD_API}/api/pools/${POOL_NAME}/release/${sessionName}`;
  try {
    navigator.sendBeacon(url, new Blob([], { type: 'application/json' }));
  } catch {
    fetch(url, { method: 'POST', keepalive: true }).catch(() => {});
  }
}

function touchPool(sessionName: string): void {
  fetch(`${CLOUD_API}/api/pools/${POOL_NAME}/touch/${sessionName}`, { method: 'POST' }).catch(() => {});
}

// ──────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────

function sleep(ms: number): Promise<void> {
  return new Promise(r => setTimeout(r, ms));
}

function showInAppBrowserBanner(appName: string): void {
  let overlay = document.getElementById('inAppBrowserOverlay');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'inAppBrowserOverlay';
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(10,10,15,0.92);z-index:99999;display:flex;align-items:center;justify-content:center;padding:20px;backdrop-filter:blur(8px)';
    overlay.innerHTML = `
      <div style="background:#1a1b26;border:1px solid #C4A96A;border-radius:16px;padding:24px;max-width:400px;color:#c0caf5;font-family:-apple-system,sans-serif">
        <h2 style="color:#C4A96A;margin:0 0 12px;font-size:18px">Login im ${appName}-Browser nicht möglich</h2>
        <p style="font-size:14px;line-height:1.5;margin:0 0 16px">Google blockt die Anmeldung in eingebetteten Browsern. Bitte öffne diesen Link im echten Browser (Safari oder Chrome):</p>
        <div style="background:#0a0a0f;border:1px solid #24283b;border-radius:8px;padding:10px;font-size:12px;font-family:monospace;color:#7aa2f7;word-break:break-all;margin-bottom:16px" id="inAppBrowserUrl"></div>
        <button id="inAppBrowserCopy" style="width:100%;background:#C4A96A;border:none;color:#0a0a0f;font-weight:600;padding:12px;border-radius:8px;cursor:pointer;font-size:14px;margin-bottom:8px">URL kopieren</button>
        <p style="font-size:12px;color:#565f89;line-height:1.5;margin:0 0 12px">Tipp: In ${appName} oben rechts auf <strong>⋯</strong> tippen → <strong>"In Browser öffnen"</strong> oder <strong>"In Safari öffnen"</strong>.</p>
        <button id="inAppBrowserClose" style="width:100%;background:transparent;border:1px solid #24283b;color:#565f89;padding:8px;border-radius:8px;cursor:pointer;font-size:13px">Schließen</button>
      </div>
    `;
    document.body.appendChild(overlay);
  }
  const url = window.location.href;
  const urlEl = overlay.querySelector<HTMLElement>('#inAppBrowserUrl');
  if (urlEl) urlEl.textContent = url;
  const copyBtn = overlay.querySelector<HTMLButtonElement>('#inAppBrowserCopy');
  copyBtn?.addEventListener('click', () => {
    navigator.clipboard?.writeText(url).then(() => {
      copyBtn.textContent = t('toast.copied');
      setTimeout(() => { copyBtn.textContent = t('toast.copyUrl'); }, 2000);
    }).catch(() => {
      copyBtn.textContent = t('toast.copyFailed');
    });
  });
  overlay.querySelector('#inAppBrowserClose')?.addEventListener('click', () => {
    overlay!.remove();
  });
  overlay.style.display = 'flex';
}

/** Show a transient toast notification at the bottom of the screen.
 *  Used for user-facing errors that would otherwise stay invisible (e.g.
 *  mic permission denied) — better than silently swallowing the failure. */
function showToast(text: string, durationMs: number = 4500): void {
  let toast = document.getElementById('appToast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'appToast';
    toast.style.cssText = 'position:fixed;bottom:80px;left:50%;transform:translateX(-50%);background:#1a1b26;color:#c0caf5;padding:12px 18px;border-radius:12px;border:1px solid #f7768e;box-shadow:0 8px 32px rgba(0,0,0,0.5);font:14px -apple-system,sans-serif;max-width:88vw;text-align:center;z-index:99999;opacity:0;transition:opacity 0.25s ease;pointer-events:none';
    document.body.appendChild(toast);
  }
  toast.textContent = text;
  requestAnimationFrame(() => { toast!.style.opacity = '1'; });
  const t = (toast as any)._hideTimer as number | undefined;
  if (t) window.clearTimeout(t);
  (toast as any)._hideTimer = window.setTimeout(() => {
    toast!.style.opacity = '0';
  }, durationMs);
}

function updateStatus(text: string, detail: string, action?: { label: string; onClick: () => void }): void {
  const el = document.getElementById('statusText');
  const det = document.getElementById('statusDetail');
  const btn = document.getElementById('statusAction') as HTMLButtonElement | null;
  if (el) el.textContent = text;
  if (det) det.textContent = detail;
  if (btn) {
    // Always strip previous handlers so old actions don't linger when
    // the next updateStatus call has no action.
    const fresh = btn.cloneNode(true) as HTMLButtonElement;
    btn.parentNode?.replaceChild(fresh, btn);
    if (action) {
      fresh.textContent = action.label;
      fresh.style.display = 'inline-block';
      fresh.addEventListener('click', action.onClick);
    } else {
      fresh.style.display = 'none';
    }
  }
}

// ──────────────────────────────────────────────────────────────
// Music queue debug overlay (F3) — desktop diagnostic
// ──────────────────────────────────────────────────────────────
type DebugSongItem = { query: string; title?: string; source?: 'ai' | 'default' };
let lastSongsSnap: { current: DebugSongItem | null; queue: DebugSongItem[] } = { current: null, queue: [] };

function renderMusicDebugOverlay(): void {
  const overlay = document.getElementById('musicDebugOverlay');
  if (!overlay || overlay.style.display === 'none') return;
  const currentEl = document.getElementById('musicDebugCurrent');
  const queueEl = document.getElementById('musicDebugQueue');
  const escape = (s: string) => s.replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]!));
  const renderItem = (s: DebugSongItem | null | undefined): string => {
    if (!s) return '<li class="imm-music-debug-empty">—</li>';
    const label = escape(s.title || s.query || '(?)');
    const src = s.source || 'ai';
    const tag = src === 'ai' ? 'KI' : 'DEF';
    return `<li><span class="imm-music-src-badge ${src}">${tag}</span><span class="imm-music-track">${label}</span></li>`;
  };
  if (currentEl) currentEl.innerHTML = renderItem(lastSongsSnap.current);
  if (queueEl) {
    queueEl.innerHTML = lastSongsSnap.queue.length === 0
      ? '<li class="imm-music-debug-empty">leer</li>'
      : lastSongsSnap.queue.map(renderItem).join('');
  }
}

window.addEventListener('keydown', (e: KeyboardEvent) => {
  if (e.key !== 'F3') return;
  e.preventDefault();
  const overlay = document.getElementById('musicDebugOverlay');
  if (!overlay) return;
  overlay.style.display = overlay.style.display === 'none' ? 'block' : 'none';
  renderMusicDebugOverlay();
});

// ──────────────────────────────────────────────────────────────
// Event log debug overlay (F2) — what's happening, what came back
// ──────────────────────────────────────────────────────────────
type DebugLogEntry = { time: string; color: string; msg: string };
const debugEventBuffer: DebugLogEntry[] = [];
const DEBUG_BUFFER_MAX = 200;
function pushDebugEvent(msg: string, color: string): void {
  const t = new Date().toLocaleTimeString('de', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  debugEventBuffer.unshift({ time: t, color, msg });
  while (debugEventBuffer.length > DEBUG_BUFFER_MAX) debugEventBuffer.pop();
  renderDebugOverlay();
}
function renderDebugOverlay(): void {
  const overlay = document.getElementById('debugOverlay');
  if (!overlay || overlay.style.display === 'none') return;
  const logEl = document.getElementById('debugOverlayLog');
  const countEl = document.getElementById('debugOverlayCount');
  if (!logEl) return;
  const escape = (s: string) => s.replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]!));
  logEl.innerHTML = debugEventBuffer.map(e =>
    `<div class="imm-debug-line"><span class="imm-debug-time">${e.time}</span><span class="imm-debug-msg" style="color:${e.color}">${escape(e.msg)}</span></div>`
  ).join('');
  if (countEl) countEl.textContent = String(debugEventBuffer.length);
}
window.addEventListener('keydown', (e: KeyboardEvent) => {
  if (e.key !== 'F2') return;
  e.preventDefault();
  const overlay = document.getElementById('debugOverlay');
  if (!overlay) return;
  overlay.style.display = overlay.style.display === 'none' ? 'flex' : 'none';
  renderDebugOverlay();
});

// ──────────────────────────────────────────────────────────────
// Bootstrap
// ──────────────────────────────────────────────────────────────

async function bootstrap(): Promise<void> {
  const params = new URLSearchParams(window.location.search);

  // ── Knowledge UX-Testseite (?test=knowledge) ──
  // Simulierte Guide-Delivery: echte Items, echte Renderer, kein Bot.
  if (params.get('test') === 'knowledge') {
    const { renderKnowledgeTestPage } = await import('./knowledgeTestPage');
    renderKnowledgeTestPage();
    return;
  }

  // ── OAuth callback ──
  if (window.location.pathname === '/auth/callback') {
    const success = await handleCallback();
    window.location.href = success ? '/' : '/?auth_error=1';
    return;
  }

  // ── Step 1: Mount skin immediately (no blocking operations) ──
  const skinParam = params.get('skin');
  const appRoot = document.getElementById('app');
  if (!appRoot) throw new Error('#app not found');

  const skin = skinParam === 'admin'
    ? new DefaultSkin('pending')
    : new ImmersiveSkin('pending');
  skin.mount(appRoot);

  // ── i18n: translate the freshly mounted DOM + wire the live updater ──
  // Every element with data-i18n / data-i18n-attr is filled here so the
  // welcome screen renders in the user's language on first paint. When
  // the lang button switches later, applyDomTranslations runs again.
  applyDomTranslations();
  window.addEventListener('wanderlaut:lang-changed', () => applyDomTranslations());

  // ── Deko-Map hinter dem Welcome — bewusst NACH dem First Paint ──
  // mapbox-gl (~1,7MB) ist seit dem Lazy-Split nicht mehr im Entry-Bundle;
  // dieser Hintergrund darf es nicht zurück in den kritischen Boot-Pfad ziehen.
  // Kein await: Welcome ist sofort interaktiv, die Karte blendet nach, sobald
  // der Chunk da ist. Die Instanz wird vor guide.mountMap() wieder entfernt
  // (sonst stacken zwei Map-Instanzen DOM im selben <div id="map">).
  const mapboxToken = (import.meta.env.VITE_MAPBOX_TOKEN as string | undefined) || '';
  let decoMap: { remove(): void } | null = null;
  if (mapboxToken) {
    setTimeout(async () => {
      try {
        const mapboxgl = await import('mapbox-gl');
        mapboxgl.default.accessToken = mapboxToken;
        const mapContainer = document.getElementById('map');
        // Nur solange die Tour nicht läuft — sonst gehört #map dem MapRenderer.
        if (mapContainer && !guide) {
          decoMap = new mapboxgl.default.Map({
            container: mapContainer,
            style: 'mapbox://styles/mapbox/dark-v11',
            center: [14.3, 46.62],
            zoom: 3,
            attributionControl: false,
          });
        }
      } catch {
        // WebGL not available — app works without map
      }
    }, 300);
  }

  // Initialize Lucide icons. The script tag is deferred so it should be
  // available now, but if for some reason it's still loading (slow CDN,
  // tab restored from background) we retry a few times instead of leaving
  // every <i data-lucide="..."> as an empty stub.
  const tryInitLucide = (attempt: number = 0) => {
    const lu = (window as any).lucide;
    if (lu?.createIcons) {
      lu.createIcons();
    } else if (attempt < 20) {
      window.setTimeout(() => tryInitLucide(attempt + 1), 100);
    } else {
      remoteLog('Lucide failed to load after 2s — icons will not render', 'boot', 'Error');
    }
  };
  tryInitLucide();

  // ── Step 1.5: Dev-Auto-Login (kein Google in dev/E2E) ──
  // In Dev mintet die Vite-Middleware /__dev-mint einen Apopovic-Token
  // (server-side dev-mint-key → auth-api), sodass der Google-OAuth-Tanz beim
  // Testen entfällt. No-op in prod / wenn schon eingeloggt / nicht konfiguriert.
  await devAutoLogin();

  // Dev: ?lat=&lon= injects a fixed position (skips GPS via __guideOverridePosition)
  // so the realtime guide is testable on desktop / where CoreLocation fails
  // (kCLErrorLocationUnknown) — the autoStart waits for a position, GPS-less desktops
  // never get one otherwise.
  {
    const pp = new URLSearchParams(location.search);
    const la = parseFloat(pp.get('lat') || '');
    const lo = parseFloat(pp.get('lon') || '');
    if (Number.isFinite(la) && Number.isFinite(lo)) {
      (window as any).__guideOverridePosition = { lat: la, lon: lo, label: 'url-override' };
    }
  }

  // ── Step 2: Auth UI (buttons are in the center welcome area now) ──
  const signinBtn = document.getElementById('signinBtn2');
  const startBtn = document.getElementById('startBtn2');
  const userGreeting = document.getElementById('userGreeting2');
  const welcomeGearBtn = document.getElementById('welcomeGearBtn');

  if (isLoggedIn()) {
    if (signinBtn) signinBtn.style.display = 'none';
    if (startBtn) startBtn.style.display = 'inline-flex';
    const user = getUser();
    if (userGreeting && user) {
      userGreeting.style.display = 'block';
      userGreeting.textContent = t('welcome.greeting', { name: user.first_name });
    }
  } else {
    if (signinBtn) signinBtn.style.display = 'inline-flex';
    if (startBtn) startBtn.style.display = 'none';
  }

  // Dev-Convenience (Alex 2026-06-27): beim Laden das User-JWT refreshen, damit
  // ein Reload eingeloggt bleibt statt jedes Mal den Google-Login zu verlangen
  // (der access-token hat 15min TTL). Fire-and-forget; greift nur wenn ein
  // gültiger refresh-token da ist — sonst bleibt der Sign-in nötig.
  if (isLoggedIn()) void refreshToken();

  // Dev-Agent-Selector (DEV-only): Dropdown im Welcome-Screen zur Guide-
  // Engine-Wahl. Self-contained, lazy, setzt ?session=/?pool=/?realtime= + reload.
  // Realtime ist jetzt EINE wählbare Engine in dieser Liste (⚡ schnell, OpenAI-
  // Stimme) neben den GuideAgent-Varianten — der Selector bleibt also auch im
  // Realtime-Modus sichtbar, um zurück auf einen GuideAgent zu wechseln.
  if (DEV_AGENT_SELECTOR) {
    void import('./devAgentSelector')
      .then((m) => m.initDevAgentSelector({
        deepDive: {
          get: () => {
            const s = getStoredPrefs().deepDive;
            return { depth: s?.depth ?? 0, layerPauseSec: s?.layerPauseSec ?? 4 };
          },
          set: (depth, layerPauseSec) => {
            setStoredPrefs({ deepDive: { depth, layerPauseSec } });
            applyDeepDive(guide); // live if running; else applied at guide start
          },
        },
      }))
      .catch(() => { /* dev tool, optional */ });
  }

  // Age chip — visible when feature is on AND user is logged in.
  // Stores BIRTHDATE (not age) so the chip stays accurate forever.
  const ageChip = document.getElementById('ageChip') as HTMLButtonElement | null;
  const ageChipLabel = document.getElementById('ageChipLabel');
  const renderAgeChip = () => {
    if (!ageChip || !ageChipLabel) return;
    if (!AGE_FEATURE_ENABLED || !isLoggedIn()) {
      ageChip.style.display = 'none';
      return;
    }
    ageChip.style.display = 'inline-flex';
    const bday = getStoredBirthdate();
    const age = bday ? computeAge(bday) : null;
    if (age === null) {
      ageChip.classList.add('empty');
      ageChipLabel.textContent = t('welcome.ageEnter');
    } else {
      ageChip.classList.remove('empty');
      ageChipLabel.textContent = t('welcome.ageYears', { age });
    }
  };
  renderAgeChip();

  // Birthdate modal — horizontal year-of-birth wheel
  const bdayOverlay = document.getElementById('bdayOverlay');
  const yearStrip = document.getElementById('yearStrip');
  const yearAgeDisplay = document.getElementById('yearAgeDisplay');
  const yearDecadeTip = document.getElementById('yearDecadeTip');
  const currentYear = new Date().getFullYear();
  const MIN_YEAR = 1920;
  const MAX_YEAR = currentYear; // can't be born in the future

  // Build the strip once
  if (yearStrip && yearStrip.childElementCount === 0) {
    const frag = document.createDocumentFragment();
    for (let y = MIN_YEAR; y <= MAX_YEAR; y++) {
      const b = document.createElement('button');
      b.className = 'imm-year-item' + (y % 10 === 0 ? ' decade-anchor' : '');
      b.textContent = String(y);
      b.dataset.year = String(y);
      frag.appendChild(b);
    }
    yearStrip.appendChild(frag);
    yearStrip.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (t.classList.contains('imm-year-item')) {
        const y = parseInt(t.dataset.year || '0', 10);
        scrollYearToCenter(y, true);
      }
    });
  }

  let currentSelectedYear: number | null = null;
  const decadeLabel = (y: number) => {
    const decade = Math.floor(y / 10) * 10;
    if (decade < 1950) return 'Vorkriegszeit';
    if (decade < 1970) return 'Nachkriegszeit';
    if (decade < 1990) return `${decade}er Jahre`;
    if (decade < 2010) return `${decade}er`;
    return 'Generation Z';
  };
  const updateActiveYear = () => {
    if (!yearStrip) return;
    const containerCenter = yearStrip.scrollLeft + yearStrip.clientWidth / 2;
    let bestEl: HTMLButtonElement | null = null;
    let bestDist = Infinity;
    yearStrip.querySelectorAll<HTMLButtonElement>('.imm-year-item').forEach(el => {
      const center = el.offsetLeft + el.offsetWidth / 2;
      const dist = Math.abs(center - containerCenter);
      if (dist < bestDist) { bestDist = dist; bestEl = el; }
    });
    yearStrip.querySelectorAll('.imm-year-item').forEach(el => el.classList.remove('active'));
    if (bestEl) {
      (bestEl as HTMLElement).classList.add('active');
      const y = parseInt((bestEl as HTMLElement).dataset.year || '0', 10);
      currentSelectedYear = y;
      const age = currentYear - y;
      if (yearAgeDisplay) yearAgeDisplay.textContent = String(age);
      if (yearDecadeTip) yearDecadeTip.textContent = decadeLabel(y);
    }
  };
  let scrollDebounce: number | null = null;
  yearStrip?.addEventListener('scroll', () => {
    if (scrollDebounce !== null) window.clearTimeout(scrollDebounce);
    scrollDebounce = window.setTimeout(updateActiveYear, 60);
  });

  const scrollYearToCenter = (year: number, smooth: boolean) => {
    if (!yearStrip) return;
    const clamped = Math.max(MIN_YEAR, Math.min(MAX_YEAR, year));
    const el = yearStrip.querySelector<HTMLButtonElement>(
      `.imm-year-item[data-year="${clamped}"]`,
    );
    if (!el) return;
    const target = el.offsetLeft + el.offsetWidth / 2 - yearStrip.clientWidth / 2;
    yearStrip.scrollTo({ left: target, behavior: smooth ? 'smooth' : 'auto' });
    // Force a synchronous active-state update so the user sees feedback even
    // before the smooth-scroll settles.
    window.setTimeout(updateActiveYear, smooth ? 350 : 0);
  };

  // Body-scroll lock: while the modal is open we save the current
  // scrollY, freeze the body with position:fixed and restore on close.
  // Without this iOS Safari interprets vertical touchmove on the wheel
  // as a page-scroll and the modal drifts up/down.
  let bdayPreLockScrollY = 0;
  const lockBodyScroll = () => {
    bdayPreLockScrollY = window.scrollY;
    document.body.style.top = `-${bdayPreLockScrollY}px`;
    document.body.classList.add('bday-modal-open');
  };
  const unlockBodyScroll = () => {
    document.body.classList.remove('bday-modal-open');
    document.body.style.top = '';
    window.scrollTo(0, bdayPreLockScrollY);
  };

  // Active rejection of any non-horizontal touch on the year strip.
  // touch-action: pan-x is not enough on iOS when the strip is itself
  // a scroll container — we have to intercept touchmove and only allow
  // horizontal deltas. Vertical movement gets preventDefault().
  let touchStartX = 0;
  let touchStartY = 0;
  yearStrip?.addEventListener('touchstart', (e: TouchEvent) => {
    if (e.touches.length !== 1) return;
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
  }, { passive: true });
  yearStrip?.addEventListener('touchmove', (e: TouchEvent) => {
    if (e.touches.length !== 1) return;
    const dx = Math.abs(e.touches[0].clientX - touchStartX);
    const dy = Math.abs(e.touches[0].clientY - touchStartY);
    // If the gesture is more vertical than horizontal, block it. Means
    // the wheel only reacts to actual horizontal swipes.
    if (dy > dx) e.preventDefault();
  }, { passive: false });

  const openBdayModal = () => {
    if (!bdayOverlay) return;
    lockBodyScroll();
    bdayOverlay.style.display = 'flex';
    const bday = getStoredBirthdate();
    const initialYear = bday
      ? parseInt(bday.slice(0, 4), 10)
      : currentYear - 40;
    window.setTimeout(() => scrollYearToCenter(initialYear, false), 50);
  };
  const closeBdayModal = () => {
    if (bdayOverlay) bdayOverlay.style.display = 'none';
    unlockBodyScroll();
  };
  ageChip?.addEventListener('click', openBdayModal);
  document.getElementById('bdayCancelBtn')?.addEventListener('click', closeBdayModal);
  bdayOverlay?.addEventListener('click', (e) => {
    if (e.target === bdayOverlay) closeBdayModal();
  });
  document.getElementById('bdaySaveBtn')?.addEventListener('click', () => {
    if (currentSelectedYear === null) {
      showToast('Bitte ein Geburtsjahr wählen.');
      return;
    }
    // Store as YYYY-01-01 — computeAge() handles month/day rollover
    // gracefully (uses Jan 1 as DOB, so age is correct from Jan 1 onwards).
    const iso = `${currentSelectedYear}-01-01`;
    setStoredBirthdate(iso);
    remoteLog(`Birth year set: ${currentSelectedYear} (age ${currentYear - currentSelectedYear})`, 'profile');
    renderAgeChip();
    closeBdayModal();
  });
  document.getElementById('bdayClearBtn')?.addEventListener('click', () => {
    setStoredBirthdate(null);
    remoteLog('Birthdate cleared', 'profile');
    renderAgeChip();
    closeBdayModal();
  });

  signinBtn?.addEventListener('click', () => {
    const { inApp, appName } = isInAppBrowser();
    if (inApp) {
      showInAppBrowserBanner(appName);
      return;
    }
    void startGoogleLogin();
  });
  welcomeGearBtn?.addEventListener('click', () => {
    const modal = document.getElementById('immSettingsModal');
    if (modal) modal.style.display = modal.style.display === 'none' ? 'block' : 'none';
  });

  // ── Debug overlay ──
  const debugOverlay = document.getElementById('debugOverlay');
  const debugLog = document.getElementById('debugLog');
  const debugStatus = document.getElementById('debugStatus');
  const debugClose = document.getElementById('debugClose');
  const debugToggleBtn = document.getElementById('debugToggleBtn');
  const showDebug = params.has('debug');
  if (showDebug && debugOverlay) debugOverlay.style.display = 'block';

  const toggleDebug = () => {
    if (debugOverlay) debugOverlay.style.display = debugOverlay.style.display === 'none' ? 'block' : 'none';
  };
  debugClose?.addEventListener('click', toggleDebug);
  debugToggleBtn?.addEventListener('click', () => {
    // Close settings modal, open debug
    const modal = document.getElementById('immSettingsModal');
    if (modal) modal.style.display = 'none';
    toggleDebug();
  });
  // Debug button in top-right controls (below map/image toggle)
  document.getElementById('debugToggleBtn2')?.addEventListener('click', toggleDebug);

  // Triple-tap top-left to toggle debug
  let debugTaps = 0;
  document.addEventListener('click', (e) => {
    if (e.clientX < 60 && e.clientY < 60) {
      debugTaps++;
      setTimeout(() => { debugTaps = 0; }, 600);
      if (debugTaps >= 3) { toggleDebug(); debugTaps = 0; }
    }
  });

  // Pipeline status indicators (compact pills at top of debug overlay)
  const statusPills: Record<string, HTMLElement> = {};
  function setStatus(key: string, label: string, state: 'ok' | 'warn' | 'err' | 'wait'): void {
    if (!debugStatus) return;
    const colors = { ok: '#9ece6a', warn: '#e0af68', err: '#f7768e', wait: '#565f89' };
    if (!statusPills[key]) {
      const pill = document.createElement('span');
      pill.style.cssText = `padding:2px 6px;border-radius:6px;font-size:9px;font-weight:700;border:1px solid ${colors[state]}33`;
      debugStatus.appendChild(pill);
      statusPills[key] = pill;
    }
    const pill = statusPills[key];
    pill.textContent = label;
    pill.style.color = colors[state];
    pill.style.borderColor = colors[state] + '33';
    pill.style.background = colors[state] + '15';
  }

  function dbg(msg: string, color = '#9ece6a'): void {
    // Always feed the F2 overlay buffer, even when the legacy debugLog
    // element isn't present in the current skin.
    pushDebugEvent(msg, color);
    if (!debugLog) return;
    const t = new Date().toLocaleTimeString('de', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const line = document.createElement('div');
    line.innerHTML = `<span style="color:#565f89">${t}</span> <span style="color:${color}">${msg}</span>`;
    debugLog.prepend(line);
    while (debugLog.children.length > 80) debugLog.removeChild(debugLog.lastChild!);
  }

  // ── Settings: wire buttons immediately (before guide exists) ──
  // Store pending values — applied to guide after init in wireEvents()
  // Hydrate defaults from persisted prefs (last-visit values) so the
  // onboarding UI starts where the user left off. Falls back to the
  // hardcoded defaults when no prefs are stored yet.
  const storedPrefs = getStoredPrefs();
  let pendingTransport = storedPrefs.transport || 'walking';
  let pendingMode = storedPrefs.mode || 'culture';
  let pendingLang = storedPrefs.language || 'de';

  // Settings-modal markup defaults to `.active` on the "walking"
  // button — but if a previous session stored a different transport
  // in prefs, sync that to the modal BEFORE wiring click handlers so
  // the visible highlight matches the actual pendingTransport state.
  document.querySelectorAll<HTMLButtonElement>('.transport-btn').forEach(b => {
    b.classList.toggle('active', b.dataset.transport === pendingTransport);
  });

  document.querySelectorAll<HTMLButtonElement>('.transport-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.transport-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      pendingTransport = btn.dataset.transport || 'walking';
      if (guide) guide.setTransport(pendingTransport as any);
    });
  });

  // ── deep_dive Tiefen-Schichten (Content #1213/#1214) — Settings-Controls ──
  // Wie der User das Guiding fährt: Schicht-Anzahl + Pause beim Verweilen.
  // Schreibt StoredPrefs.deepDive + applyDeepDive (live wenn Guide läuft, sonst
  // beim Guide-Start angewendet). EINE Config-Quelle, gleiche wie URL/Dev-Panel.
  const ddCurrent = (): { depth: number; layerPauseSec: number } => {
    const s = getStoredPrefs().deepDive;
    return { depth: s?.depth ?? 0, layerPauseSec: s?.layerPauseSec ?? 4 };
  };
  const ddSlider = document.getElementById('depthPauseSlider') as HTMLInputElement | null;
  const ddLabel = document.getElementById('depthPauseLabel');
  {
    const dd = ddCurrent();
    document.querySelectorAll<HTMLButtonElement>('.depth-btn').forEach(b =>
      b.classList.toggle('active', Number(b.dataset.depth) === dd.depth));
    if (ddSlider) ddSlider.value = String(dd.layerPauseSec);
    if (ddLabel) ddLabel.textContent = `${dd.layerPauseSec}s`;
  }
  document.querySelectorAll<HTMLButtonElement>('.depth-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.depth-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      setStoredPrefs({ deepDive: { depth: Number(btn.dataset.depth) || 0, layerPauseSec: ddCurrent().layerPauseSec } });
      applyDeepDive(guide);
    });
  });
  ddSlider?.addEventListener('input', () => {
    const sec = Number(ddSlider.value) || 0;
    if (ddLabel) ddLabel.textContent = `${sec}s`;
    setStoredPrefs({ deepDive: { depth: ddCurrent().depth, layerPauseSec: sec } });
    applyDeepDive(guide);
  });

  // Sync mode-btn active state to pendingMode at boot — same pattern
  // as transport-btn and lang-btn above. Without this, the markup
  // default ("culture" highlighted in template.html) survives even
  // after the user has stored a different mode in prefs, which
  // makes the Settings highlight lie about what the bot will use.
  // First-time users (empty prefs) land on "culture" anyway because
  // that's the fallback in `let pendingMode = storedPrefs.mode || 'culture'`.
  document.querySelectorAll<HTMLButtonElement>('.mode-btn').forEach(b => {
    b.classList.toggle('active', b.dataset.mode === pendingMode);
  });

  document.querySelectorAll<HTMLButtonElement>('.mode-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.mode-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      pendingMode = btn.dataset.mode || 'culture';
      if (guide) guide.setMode(pendingMode as any);
    });
  });
  // Sync lang button active state to the i18n-detected/persisted language
  // at boot, BEFORE wiring click handlers — otherwise a leftover .active
  // on the markup default ("de") would conflict with the user's stored
  // choice the moment they reload the app.
  {
    const initialLang = getLang();
    pendingLang = initialLang;
    document.querySelectorAll<HTMLButtonElement>('.lang-btn').forEach(b => {
      b.classList.toggle('active', b.dataset.lang === initialLang);
    });
  }
  document.querySelectorAll<HTMLButtonElement>('.lang-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.lang-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const code = (btn.dataset.lang || 'de') as LangCode;
      pendingLang = code;
      // Update UI language — this re-runs applyDomTranslations() via
      // the wanderlaut:lang-changed listener we wired in mount().
      void setLang(code);
      // Bot-response language is tracked separately so a later phase
      // can still ask the bot in DE while the UI is in EN/ZH/etc.
      // Today we mirror them: switching UI language also switches the
      // bot's narration language until the master-DE-plus-translate
      // backend pipeline lands.
      if (guide) guide.setLanguage(code);
    });
  });

  // AI-Engine toggle — Pool (Codex via cloud-api) vs Direct (OpenAI via
  // guide-api). Persisted to localStorage, applied to the guide instance
  // both on init (below) and immediately when the user flips the toggle.
  const AI_MODE_STORAGE_KEY = 'wanderlaut-ai-mode';
  const DIRECT_ENDPOINT = `${GUIDE_API_BASE}/ai/generate`;
  const storedAiMode = (localStorage.getItem(AI_MODE_STORAGE_KEY) as 'pool' | 'direct' | null) || 'pool';
  let pendingAiMode: 'pool' | 'direct' = storedAiMode === 'direct' ? 'direct' : 'pool';
  document.querySelectorAll<HTMLButtonElement>('.ai-mode-btn').forEach(btn => {
    // Hydrate initial active state from persisted value
    btn.classList.toggle('active', btn.dataset.aiMode === pendingAiMode);
    btn.addEventListener('click', () => {
      document.querySelectorAll('.ai-mode-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      pendingAiMode = (btn.dataset.aiMode === 'direct') ? 'direct' : 'pool';
      localStorage.setItem(AI_MODE_STORAGE_KEY, pendingAiMode);
      if (guide) guide.setAiMode(pendingAiMode, DIRECT_ENDPOINT);
      remoteLog(`AI mode → ${pendingAiMode}`, 'ai-mode');
    });
  });

  // ── HMR cleanup: kill any pre-existing guide instance from a stale
  // module before we wire new state. Without this, Vite's hot module
  // reload (every code edit on dev) leaves the previous AudioGuide's
  // setInterval ticking — it keeps firing GPS-updates to the bot even
  // after the user clicks Stop on the new instance, because Stop only
  // controls the live closure scope. Symptom: ticks keep arriving at
  // GuideTest with the user's stable client_id long after the visible
  // tab is paused. ──
  if ((window as any).__activeGuide) {
    try {
      const prev = (window as any).__activeGuide as AudioGuide;
      prev.stop();
      remoteLog('HMR: stopped stale AudioGuide instance from previous module', 'hmr');
    } catch (e) {
      remoteLog(`HMR: stale-instance stop failed: ${e}`, 'hmr', 'Warning');
    }
    (window as any).__activeGuide = null;
  }
  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      const g = (window as any).__activeGuide as AudioGuide | undefined;
      if (g) {
        try { g.stop(); } catch { /* ignore */ }
        (window as any).__activeGuide = null;
      }
    });
  }

  // ── Step 3: On START — acquire pool, init guide, start tour ──
  let guide: AudioGuide | null = null;
  let sessionName = '';
  let guideSessionId: number | null = null;
  let lastCity = '';
  let lastLat = 0;
  let lastLon = 0;
  // Outer-scope reference to the beforeunload-handler so user:stop and
  // dead-session-detect (both in different inner closures) can
  // removeEventListener it. Initialized inside startTour() after pool
  // acquire; stays null until then.
  let onBeforeUnloadRelease: (() => void) | null = null;
  const sessionState = new SessionStateManager();
  const sessionOverride = params.get('session');
  // Single-tester mode (2026-06-09): bypass pool acquire by default and
  // target a persistent session Alex can clear + inspect + integrate
  // into prompt-improvement workflow. URL controls:
  //   ?session=NAME     → use NAME directly (no acquire/release)
  //   ?pool=true        → opt back into pool acquire (multi-user mode)
  //   no params         → use FIXED_SESSION_NAME (no acquire/release)
  // Set FIXED_SESSION_NAME='' to make pool the federation-wide default.
  const FIXED_SESSION_NAME = 'GuideTest';
  const poolOptIn = (params.get('pool') || '').toLowerCase();
  const usePoolParam = poolOptIn === 'true' || poolOptIn === '1';
  // usingPool drives the release/touch lifecycle calls — true ONLY when
  // we actually acquired from the pool (explicit ?pool=true, or no
  // fixed default). Explicit ?session=NAME never uses pool either.
  const usingPool = !sessionOverride && (usePoolParam || !FIXED_SESSION_NAME);

  let tourStarting = false;
  async function startTour(): Promise<void> {
    // Re-Entry-Guard: ein Retry nach Fehler (Start-Button kommt zurück) oder ein
    // Doppel-Tap darf KEINE zweite AudioGuide-Instanz neben der ersten hochziehen
    // (doppelte GPS-Watches/Intervalle/Audio, Leck). Läuft schon ein Start,
    // ignorieren; existiert eine alte Instanz, wird sie vor der neuen gestoppt.
    if (tourStarting) {
      dbg('startTour ignoriert — Start läuft bereits', '#f7768e');
      return;
    }
    tourStarting = true;
    if (guide) {
      try {
        guide.stop();
      } catch {
        /* alte Instanz best-effort stoppen — ein Fehler darf den Neustart nicht blocken */
      }
    }
    // Immediately hide ALL welcome elements
    const welcomeContent = document.getElementById('welcomeContent');
    const statusContent = document.getElementById('statusContent');
    const intentArea = document.getElementById('intentArea');
    const centerArea = document.getElementById('centerArea');
    if (welcomeContent) welcomeContent.style.display = 'none';
    if (intentArea) intentArea.style.display = 'none';
    if (statusContent) statusContent.style.display = 'block';
    if (startBtn) startBtn.style.display = 'none';
    if (signinBtn) signinBtn.style.display = 'none';
    if (userGreeting) userGreeting.style.display = 'none';
    updateStatus(t('status.connecting'), t('status.connectingDetail'));
    dbg('▶ startTour()', '#C4A96A');

    // Show best nearby interaction as warmup preview (non-blocking, public).
    // If the user picked a non-GPS location in onboarding (surprise / custom
    // city), use those coords for the warmup instead of real GPS — otherwise
    // the user sees a Vienna preview while having requested Salzburg.
    const override = (window as any).__guideOverridePosition as
      { lat: number; lon: number; label: string } | undefined;
    // GPS prompt is much slower on desktop than mobile — the browser
    // permission dialog can sit open for 10-30s while the user reads it.
    // We tag the resolved value with WHY so the .then handler can tell
    // "denied" apart from "user is still thinking", and only show the
    // status banner in the former case. Permissions API gives us a
    // pre-check (denied → no point waiting at all).
    type GpsResult = { lat: number; lon: number } | { reason: 'denied' | 'unavailable' | 'timeout' };
    const quickGps = new Promise<GpsResult>(resolve => {
      if (override) {
        resolve({ lat: override.lat, lon: override.lon });
        return;
      }
      if (!navigator.geolocation) { resolve({ reason: 'unavailable' }); return; }

      let done = false;
      const settle = (v: GpsResult) => { if (done) return; done = true; resolve(v); };

      // 30s ceiling — covers slow desktop permission prompts but won't
      // hang forever if the user closes the prompt without choosing.
      const HARD_TIMEOUT_MS = 30000;
      const hardTimeout = setTimeout(() => settle({ reason: 'timeout' }), HARD_TIMEOUT_MS);

      // Pre-check permission state if the API is available — lets us
      // bail out instantly on a previously-denied site without showing
      // a 30-second-long "Standort nicht freigegeben" hint.
      const navAny = navigator as any;
      if (navAny.permissions && typeof navAny.permissions.query === 'function') {
        navAny.permissions.query({ name: 'geolocation' }).then((p: any) => {
          if (p?.state === 'denied') { clearTimeout(hardTimeout); settle({ reason: 'denied' }); }
        }).catch(() => { /* fall through to getCurrentPosition */ });
      }

      navigator.geolocation.getCurrentPosition(
        pos => { clearTimeout(hardTimeout); settle({ lat: pos.coords.latitude, lon: pos.coords.longitude }); },
        err => {
          clearTimeout(hardTimeout);
          // err.code: 1=denied, 2=unavailable, 3=timeout
          const reason = err.code === 1 ? 'denied' : err.code === 3 ? 'timeout' : 'unavailable';
          settle({ reason });
        },
        { timeout: HARD_TIMEOUT_MS, maximumAge: 300000 },
      );
    });
    let warmupDismissed = false;
    let warmupAudioBuffer: ArrayBuffer | null = null;
    type WarmupTopic = { title: string; subtitle: string; image: string; imageSize: string; lat?: number; lon?: number; wikipedia?: string };
    let warmupTopicImage: WarmupTopic | null = null;
    quickGps.then(result => {
      const hasPos = 'lat' in result;
      dbg(`Warmup GPS: ${hasPos ? `${result.lat.toFixed(3)},${result.lon.toFixed(3)}` : (result as any).reason}`, '#565f89');
      remoteLog(`Warmup GPS: ${hasPos ? `${result.lat.toFixed(3)},${result.lon.toFixed(3)}` : (result as any).reason}`, 'warmup');
      if (!hasPos) {
        // Only nag the user when the permission was actually denied or
        // the device has no geolocation. A 30s timeout (user dismissed
        // the prompt without choosing) shows the same neutral hint;
        // anything else means GPS was actively refused.
        const reason = (result as any).reason as 'denied' | 'unavailable' | 'timeout';
        const openCustomCity = {
          label: t('status.enterCity'),
          onClick: () => window.dispatchEvent(new CustomEvent('wanderlaut:open-custom-city')),
        };
        if (reason === 'denied') {
          updateStatus(
            t('status.locationBlocked'),
            t('status.locationBlockedDetail'),
            openCustomCity,
          );
        } else if (reason === 'unavailable') {
          updateStatus(
            t('status.locationUnavailable'),
            t('status.locationUnavailableDetail'),
            openCustomCity,
          );
        }
        // 'timeout' → say nothing; user may still grant later via prompt
        return null;
      }
      return getNearbyInteraction(result.lat, result.lon, pendingLang, pendingMode);
    }).then(prev => {
      if (!prev) { dbg('Warmup: no nearby entry found', '#565f89'); return; }
      if (warmupDismissed) return;
      remoteLog(`Warmup: "${prev.topic_title}" audio=${!!prev.audio_url} img=${!!prev.topic_image_url}`, 'warmup');
      const panel = document.getElementById('audioPlayerCard');
      const title = document.getElementById('playerTitle');
      const cat = document.getElementById('playerCategory');
      const loc = document.getElementById('playerLocation');
      const text = document.getElementById('responseText');
      const topicImg = document.getElementById('topicImg') as HTMLImageElement | null;
      const topicImgWrap = document.getElementById('topicImgWrap');
      const actionsEl = document.getElementById('actionsArea');
      if (title) title.textContent = prev.topic_title || '';
      if (cat) cat.textContent = t('topic.fromArea');
      if (loc) loc.textContent = prev.city || '';
      if (text) text.textContent = prev.content?.slice(0, 150) + '…' || '';
      if (panel) {
        panel.style.display = 'block';
        // Push tour controls above the panel
        requestAnimationFrame(() => {
          if (actionsEl && panel.offsetHeight > 0) {
            actionsEl.style.bottom = `${panel.offsetHeight + 8}px`;
            document.documentElement.style.setProperty('--footer-h', `${panel.offsetHeight}px`);
          }
        });
      }
      // Show topic image if available
      if (prev.topic_image_url && topicImg && topicImgWrap) {
        topicImg.src = prev.topic_image_url;
        topicImgWrap.style.display = 'flex';
        document.body.setAttribute('data-view', 'topic');
      }
      // Stash topic data for later — emit topic:changed once guide exists.
      // This drives view-toggle, map narrative-focus marker, AND wikipedia link.
      warmupTopicImage = {
        title: prev.topic_title,
        subtitle: prev.city || '',
        image: prev.topic_image_url || '',
        imageSize: 'hero',
        lat: prev.lat,
        lon: prev.lon,
        wikipedia: prev.topic_wikipedia || undefined,
      };
      remoteLog(
        `Warmup: History-Eintrag „${prev.topic_title}" (${prev.audio_url ? 'mit' : 'ohne'} gecachtem Audio) als Sofort-Karte`,
        'strategy',
      );
      // Pre-fetch cached TTS audio — stored in warmupAudioBuffer,
      // enqueued after guide.primeAudio() in startTour().
      //
      // When the lookup served a translated content (different lang
      // than the row's master, e.g. user UI=en but row is de) the
      // backend nulls audio_url because the cached MP3 sounds in the
      // master language, not the translated one. Fall through to a
      // fresh TTS call against /api/tts-speak using the (translated)
      // content text — OpenAI picks the voice from the text language.
      if (prev.audio_url) {
        fetch(prev.audio_url).then(r => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.arrayBuffer();
        }).then(buf => {
          if (warmupDismissed) return;
          warmupAudioBuffer = buf;
          dbg(`Warmup TTS (cached): ${(buf.byteLength / 1024).toFixed(0)}KB ready`, '#565f89');
          remoteLog(`Warmup TTS cached audio fetched: ${(buf.byteLength / 1024).toFixed(0)}KB`, 'warmup');
        }).catch(err => {
          remoteLog(`Warmup TTS cached audio FETCH-FAILED: ${err}`, 'warmup', 'Warning');
        });
      } else if (prev.content && prev.content.trim()) {
        // Live-TTS for the translated content. Gleiche Basis wie der reguläre
        // TTS-Pfad (CLOUD_API-Konstante) — die frühere Env-Var
        // VITE_CLOUD_API_BOOTSTRAP ist nirgends gesetzt, ergab '' → relative
        // URL → 404 auf dem Dev-Host (traf jeden Warmup ohne gecachtes Audio).
        fetch(`${CLOUD_API}/api/tts-speak`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: prev.content }),
        }).then(r => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.arrayBuffer();
        }).then(buf => {
          if (!buf || warmupDismissed) return;
          warmupAudioBuffer = buf;
          dbg(`Warmup TTS (live ${pendingLang}): ${(buf.byteLength / 1024).toFixed(0)}KB ready`, '#565f89');
          remoteLog(`Warmup TTS live-fetched (${pendingLang}): ${(buf.byteLength / 1024).toFixed(0)}KB`, 'warmup');
        }).catch(err => {
          remoteLog(`Warmup TTS live-FETCH-FAILED: ${err}`, 'warmup', 'Warning');
        });
      } else {
        remoteLog(`Warmup: no audio source (audio_url=null and content empty)`, 'warmup', 'Warning');
      }
      dbg(`Warmup: "${prev.topic_title}" (${prev.city}) audio=${prev.audio_url ? 'cached' : 'live'}`, '#565f89');
    }).catch(() => {});

    try {
      // Resolve session: explicit URL override > pool opt-in > fixed
      // single-tester session > legacy pool acquire. Pool path keeps
      // the retry loop + acquire-trace remoteLog from acquirePool().
      if (isRealtimeMode()) {
        // Realtime ist DIE Engine (Single-Engine-Mode): KEIN Pool-Agent, KEIN
        // IACP-Send-Loop. GPS-Tracking läuft weiter und füttert die Realtime-
        // Session; die Narration kommt von OpenAI, nicht vom Pool. setRealtime-
        // Mode(true) unten unterdrückt _sendAndListen ab dem ersten Fix, damit
        // nichts parallel läuft. (Behebt den "fühlt sich parallel an"-Bug.)
        sessionName = 'realtime';
        remoteLog('Realtime engine: pool-acquire übersprungen (single-engine)', 'realtime');
      } else if (sessionOverride) {
        sessionName = sessionOverride;
      } else if (usingPool) {
        sessionName = await acquirePool();
      } else if (FIXED_SESSION_NAME) {
        sessionName = FIXED_SESSION_NAME;
        remoteLog(`Single-tester mode: using fixed session ${sessionName}`, 'pool');
      } else {
        sessionName = await acquirePool();
      }
      remoteLog(`Pool acquired: ${sessionName}`, 'pool');
      dbg(`✅ Pool: ${sessionName}`);
      updateStatus(t('status.guideStarting'), t('status.guideStartingDetail', { session: sessionName }));

      // Init guide
      const mapboxToken = (import.meta.env.VITE_MAPBOX_TOKEN as string | undefined) || '';
      const config: AudioGuideConfig = {
        session: sessionName,
        server: '',
        mapboxToken,
        cloudApiBootstrap: CLOUD_API,
        // Route GPS updates through guide-api so the track_enricher
        // adds <places>+<artrack_context> server-side (eliminates the
        // probe-vs-live XML drift). Override via VITE_GUIDE_API_BASE
        // for local guide-api dev; defaults to the arkserver prod URL.
        guideApiBase: (import.meta.env.VITE_GUIDE_API_BASE as string | undefined)
          || 'https://guide-api.arkserver.arkturian.com',
        // Fast-First-Turn: erste Narration via /live-send-fast (~2-4s sync)
        // statt 20-50s Pool-Kaltstart; guide-api wärmt den Pool parallel.
        // Abschaltbar zum Vergleich via ?fastfirst=0.
        fastFirstTurn: new URLSearchParams(location.search).get('fastfirst') !== '0',
        // Use the full OSM-context pipeline (findAll with the
        // transport-specific radius from RADIUS_PRESETS — walking
        // = 200m, bike = 2km, ...) instead of the legacy single-
        // nearest 40m mode. Default-off was leaving big-polygon
        // POIs like train stations out of the [NEAR:...] tag
        // because their centroid sits outside the 40m search; this
        // mode emits a richer [OSM:place1 | place2 | ...] tag and
        // the bot has more to anchor on.
        osmContext: true,
      };
      guide = new AudioGuide(config);
      // Track on window so the HMR-dispose handler can stop us cleanly
      // before the next module-replacement instantiates a new instance.
      (window as any).__activeGuide = guide;
      // — Route-Overlay (optional, ROUTE_OVERLAY_ENABLED) — self-contained,
      // lazy-geladen, kein Effekt auf den Live-Guide. Button rechts oben.
      if (ROUTE_OVERLAY_ENABLED) {
        const ov = Number(new URLSearchParams(location.search).get('overlayTrack'));
        const overrideTrack = Number.isFinite(ov) && ov > 0 ? ov : null;
        void import('./routeOverlay').then((m) =>
          m.initRouteOverlay(guide as any, { apiBase: config.guideApiBase || GUIDE_API_BASE, overrideTrack }),
        ).catch(() => { /* Overlay optional */ });
      }
      // — Narration-Korpus-Player (optional, NARRATION_CORPUS_ENABLED) — Modus
      // ② Statisch/ArTrack: deterministisches Abspielen des vorgebauten
      // narration_point-Korpus per GPS, kein Realtime-Agent. Self-contained,
      // lazy, Live-Pfad unangetastet.
      if (NARRATION_CORPUS_ENABLED) {
        const qs = new URLSearchParams(location.search);
        const ct = Number(qs.get('corpusTrack'));
        const overrideTrack = Number.isFinite(ct) && ct > 0 ? ct : null;
        void import('./narrationCorpus').then((m) =>
          m.initNarrationCorpus(guide as any, {
            apiBase: config.guideApiBase || GUIDE_API_BASE,
            overrideTrack,
            forceWorld: qs.get('corpusWorld') === '1',
            world: WORLD_CORPUS_ENABLED,
            staticMode: qs.get('corpusStatic') === '1',
            allowStep: qs.get('corpusStep') === '1',
            swapDelayMs: Number(qs.get('corpusSwapMs')) || undefined,
          }),
        ).catch(() => { /* Korpus-Player optional */ });
      }
      // — Realtime-Voice-Guide (optional, REALTIME_VOICE_ENABLED) — dev-gated
      // opt-in Live-Sprach-Modus. Lazy, self-contained, Live-Pfad unangetastet.
      if (REALTIME_VOICE_ENABLED) {
        // Realtime ist Engine-Default wenn als Pref gesetzt (oder ?realtime=1):
        // auto-engage sobald GPS bereit. Der 🎙-Toggle persistiert die Wahl via
        // onActiveChange → kein URL-Param nötig, einmal setzen reicht.
        void import('./realtimeVoice').then((m) => {
          m.initRealtimeVoice(guide as unknown as Parameters<typeof m.initRealtimeVoice>[0], {
            language: pendingLang,
            // Realtime endpoints live under /api/v1 — pass the full base
            // (config.guideApiBase is the host without /api/v1).
            guideApiBase: GUIDE_API_BASE,
            autoStart: isRealtimeMode(),
            // Engine-Mode: bei Realtime-Start-Fehler bleibt der Normal-Pfad
            // unterdrückt (kein laggy Fallback) — sticky suppression.
            engineMode: isRealtimeMode(),
            onActiveChange: (on: boolean) => setStoredPrefs({ realtime: on }),
          });
          // Hybrid #1233: Push-to-Talk Hold-to-Talk-Button (login-gated, ptt-mode).
          // Stumm by default, halten → Realtime-Frage; Narration bleibt ElevenLabs.
          m.initPttButton(guide as unknown as Parameters<typeof m.initPttButton>[0], {
            language: pendingLang,
            guideApiBase: GUIDE_API_BASE,
          });
        }).catch(() => { /* Realtime optional */ });
      }
      // Expose to the immersive skin so it can tap the live AnalyserNode
      // for honest waveform animation, and read AudioContext.state for
      // "audio frozen" diagnostics.
      (window as any).__audioGuide = guide;
      (window as any).__guideShowToast = showToast;
      // One-time global tap handler: if the AudioContext was suspended
      // by iOS (silent-switch toggle, OS interruption), any touch on the
      // app should resume it. Without this, "audio frozen" stays frozen
      // until the user reloads.
      const tapResume = () => {
        const ctx = guide?.audioContext;
        if (ctx && ctx.state === 'suspended') {
          ctx.resume().then(() => remoteLog('AudioContext resumed by tap', 'audio')).catch(() => {});
        }
      };
      document.addEventListener('touchstart', tapResume, { passive: true });
      document.addEventListener('click', tapResume);
      // Apply settings chosen before guide existed
      guide.setTransport(pendingTransport as any);
      guide.setMode(pendingMode as any);
      guide.setLanguage(pendingLang);
      // AI engine: pool is the default in the lib, but the user's last
      // explicit choice from localStorage wins. Without DIRECT_ENDPOINT
      // the direct path can't be reached even if the toggle says so.
      guide.setAiMode(pendingAiMode, DIRECT_ENDPOINT);
      remoteLog(`AI mode init → ${pendingAiMode}`, 'ai-mode');
      // deep_dive (Content #1213/#1214): apply persisted/URL depth + player config.
      applyDeepDive(guide);
      const dd0 = readDeepDivePrefs();
      remoteLog(`deep_dive init → depth=${dd0.depth} layerPause=${dd0.layerPauseSec}s`, 'deep-dive');
      // Live re-config knob (UI/console): persist + apply to the running guide.
      (window as Window & { __setDeepDive?: (d: number, p?: number) => void }).__setDeepDive =
        (depth: number, layerPauseSec = 4): void => {
          setStoredPrefs({ deepDive: { depth, layerPauseSec } });
          applyDeepDive(guide);
          remoteLog(`deep_dive set → depth=${depth} layerPause=${layerPauseSec}s`, 'deep-dive');
        };
      // Wire device-local HEARD callback — returns "[HEARD:...]
      // [HEARD-RECENT:...]" string for the GPS payload, built from
      // localStorage. Async signature is required by the lib but the
      // computation is synchronous in our case.
      guide.setHeardCallback(async (lat, lon) => getHeardTag(lat, lon));

      // Wire live-stream translator. Bot is forced (via prompt) to
      // emit German master regardless of UI language; this hook
      // converts content / vibe / additional_topics for the active
      // locale before topic-card + TTS render them. topic.title
      // intentionally stays literal (Eigennamen).
      guide.setTranslatorCallback(async (texts, targetLang) => {
        const { translateBatch } = await import('@/translator');
        return translateBatch(texts, targetLang, 'de');
      });

      // Record every topic the bot speaks. package:start fires when
      // a package becomes the active one (just before TTS), which
      // is exactly the moment the user "hears" the topic — multi-
      // topic responses fire it once per package, single responses
      // once total. Using the snapshot lat/lon of the user at speak
      // time so the radius check later compares "where I heard it"
      // to "where I am now", not "where the topic geographically
      // sits" (which would dedupe the wrong way for mobile users).
      {
        // Capture a non-null reference for the closure — TS narrows
        // `guide` here but loses it across the async listener boundary.
        const g = guide;
        g.eventManager.on('package:start', (p: { title?: string }) => {
          const title = p?.title;
          if (!title) return;
          const loc = g.currentLocation;
          if (!loc || loc.lat == null || loc.lon == null) return;
          pushHeard(title, loc.lat, loc.lon);
        });
      }
      // Wire image cache lookup — checks history before Wikipedia/Places
      guide.setFindImageCallback((title, lat, lon) => findReviewedImage(title, lat, lon));
      // Wire fast live image search (Commons + DDG) — between Wikipedia and Places fallback
      guide.setFindImageFastCallback(async (title, city) => {
        const r = await findImageFast(title, city || '');
        return r.image;
      });
      // Wire surprise mode picker — random city from the GeoNames pool
      // (~169k cities ≥ 1,000 inhabitants). Library falls back to
      // continental-bbox random if this returns null.
      guide.setSurprisePicker(async () => {
        const c = await getRandomCity(1000);
        if (!c) return null;
        return { name: `${c.name}, ${c.country_code}`, lat: c.lat, lon: c.lon };
      });
      // Auto-detect nearby artrack tracks. Replaces the old brute-force
      // snap-to-path loop over hardcoded ids with a single broad+narrow
      // call to /tracks/nearby. Three reliability features layered on top:
      //
      //  (1) Grid-cell cache (~110m, 60s TTL) — when the user stands
      //      still, we don't burn API calls every tick.
      //  (2) Schmitt-trigger hysteresis — once inside a track, stay
      //      inside until distance > 1.5× the on-threshold. Prevents
      //      flicker on the boundary.
      //  (3) Visibility filter 'public' — private tracks of strangers
      //      should never auto-activate on a foreign device.
      const ARTRACK_API = 'https://api-artrack.arkturian.com';
      const ON_THRESHOLD_M = 100;
      const OFF_THRESHOLD_M = 150; // 1.5× — hysteresis band
      const CACHE_TTL_MS = 60_000;
      const CACHE_CELL = 0.001; // ~110m grid

      type NearbyHit = { track_id: number; distance_m: number };
      const cellKey = (lat: number, lon: number) =>
        `${Math.round(lat / CACHE_CELL)}_${Math.round(lon / CACHE_CELL)}`;
      const nearbyCache = new Map<string, { at: number; tracks: NearbyHit[] }>();
      let currentTrackDistance = Infinity;

      guide.setTrackDetectCallback(async (lat, lon) => {
        const key = cellKey(lat, lon);
        const now = Date.now();
        const cached = nearbyCache.get(key);
        let tracks: NearbyHit[];
        if (cached && now - cached.at < CACHE_TTL_MS) {
          tracks = cached.tracks;
        } else {
          try {
            // Server filters on auto_detect_eligible=true; visibility
            // (public vs private) is intentionally NOT used here.
            const url = `${ARTRACK_API}/tracks/nearby?lat=${lat}&lon=${lon}&max_distance_m=${OFF_THRESHOLD_M}`;
            const res = await fetch(url, { headers: { 'X-API-KEY': 'Inetpass1' } });
            if (!res.ok) {
              currentTrackDistance = Infinity;
              return null;
            }
            const data = await res.json() as { tracks: NearbyHit[] };
            tracks = data.tracks || [];
            nearbyCache.set(key, { at: now, tracks });
          } catch {
            currentTrackDistance = Infinity;
            return null;
          }
        }

        const nearest = tracks[0] || null;
        if (!nearest) {
          currentTrackDistance = Infinity;
          return null;
        }

        // Schmitt-trigger: on-threshold for "enter", off-threshold for "leave".
        // If we're already inside a track (currentTrackDistance set), we
        // tolerate up to OFF_THRESHOLD_M before letting go.
        const isInside = currentTrackDistance < OFF_THRESHOLD_M;
        const threshold = isInside ? OFF_THRESHOLD_M : ON_THRESHOLD_M;
        if (nearest.distance_m <= threshold) {
          currentTrackDistance = nearest.distance_m;
          return nearest.track_id;
        }
        currentTrackDistance = Infinity;
        return null;
      });
      skin.bindEvents(guide.eventManager);
      wireEvents(guide, sessionName);

      // guide.init() (Server-Discovery, 2 Fetches) und der Map-Mount (mapbox-
      // Chunk laden + WebGL-Init) sind unabhängige Subsysteme — parallel statt
      // sequentiell spart ~1-2s auf dem kritischen Start-Pfad.
      const initPromise = guide.init();

      // Mount map — vorher die Deko-Map freigeben: zwei mapbox-Instanzen auf
      // demselben #map-Container = doppelter WebGL-Context + kaputte Interaktivität.
      if (decoMap) {
        try {
          decoMap.remove();
        } catch {
          /* best-effort */
        }
        decoMap = null;
      }
      const mapContainer = document.getElementById('map');
      // DOM-Reste einer früheren Map-Instanz räumen (Mapbox: "container should
      // be empty") — remove() sollte das tun, Gürtel-und-Hosenträger.
      if (mapContainer && mapContainer.firstChild) mapContainer.innerHTML = '';
      if (mapContainer && mapboxToken) {
        await Promise.all([initPromise, guide.mountMap(mapContainer)]);
      } else {
        await initPromise;
      }

      // Prime audio (must be in user gesture chain)
      guide.primeAudio();

      // Emit warmup topic to bus — drives view-toggle, audio panel, AND
      // map narrative focus marker (same as live Guide responses)
      if (warmupTopicImage !== null) {
        const wt = warmupTopicImage as WarmupTopic;
        guide.eventManager.emit('topic:changed', { topic: wt });
        // Also trigger map narrative focus if we have coords
        if (wt.lat && wt.lon && wt.lat !== 0) {
          guide.eventManager.emit('map:focus', {
            lat: wt.lat,
            lon: wt.lon,
            label: wt.title,
          });
        }
      }

      // Enqueue warmup cache TTS into the real pipeline (if pre-fetched).
      // Label deliberately spells out WARMUP-CACHE — without the marker
      // the debug log read as "TTS play: Cache" which was easy to mistake
      // for a live response. The user-visible signal is: audio playing
      // is from the prev-run cache, the live IACP response has NOT yet
      // arrived. When it does, _handleIacpResponse will queue real TTS
      // and replace the cached topic-card.
      //
      // Race-handling (2026-06-09): if the user clicks Start BEFORE the
      // async warmup fetch landed, warmupAudioBuffer is still null here
      // and the cache entry shows on the topic-card while the audio
      // never plays (data shown without TTS). Deferred-polling: poll up
      // to 3s for late arrival. The live iacp:response will set
      // warmupDismissed=true so a stale warmup audio doesn't talk over
      // the live response if it lands first.
      if (warmupAudioBuffer && !warmupDismissed) {
        guide.speakBuffer(warmupAudioBuffer, 'WARMUP-CACHE');
        warmupDismissed = true;
        dbg('Warmup TTS → queue (cached prev-run audio, live response pending)', '#e0af68');
        remoteLog('TTS: WARMUP-CACHE playback started (no live response yet)', 'tts');
      } else if (!warmupAudioBuffer && !warmupDismissed) {
        const startWaitTs = Date.now();
        const POLL_MS = 100;
        const MAX_WAIT_MS = 3000;
        const guideRef = guide;
        const tryDeferredWarmup = () => {
          if (warmupDismissed) return;
          if (warmupAudioBuffer) {
            guideRef.speakBuffer(warmupAudioBuffer, 'WARMUP-CACHE');
            warmupDismissed = true;
            const waited = Date.now() - startWaitTs;
            dbg(`Warmup TTS → queue (deferred ${waited}ms, fetched after Start)`, '#e0af68');
            remoteLog(
              `TTS: WARMUP-CACHE playback started (deferred buffer arrived after ${waited}ms)`,
              'tts',
            );
            return;
          }
          if (Date.now() - startWaitTs < MAX_WAIT_MS) {
            window.setTimeout(tryDeferredWarmup, POLL_MS);
          } else {
            remoteLog(
              'TTS: WARMUP-CACHE skipped — buffer did not arrive in 3s after Start. ' +
                'Either fetch failed (see preceding FETCH-FAILED entry) or backend ' +
                'returned no audio_url and content was empty.',
              'tts',
              'Warning',
            );
          }
        };
        window.setTimeout(tryDeferredWarmup, POLL_MS);
      }
      // Dismiss warmup as soon as the live response lands — any
      // still-in-flight cached audio should NOT play over the live one.
      guide.eventManager.on('iacp:response', () => {
        warmupDismissed = true;
      });

      // ── Strategie-Trail (source='strategy' in log-api) ──
      // Ein durchgehender, maschinenlesbarer Ereignis-Trail der Start-Strategie
      // (Warmup → Fast-First-Turn → Pool-Übernahme → Engine-Wechsel), damit ein
      // Live-Test hinterher exakt nachvollziehbar ist: welcher Pfad lief, mit
      // welchem Modell, wie schnell, und ob ein Fallback griff.
      const strategyT0 = Date.now();
      const sLog = (msg: string, type: 'Log' | 'Warning' | 'Error' = 'Log') =>
        remoteLog(`+${((Date.now() - strategyT0) / 1000).toFixed(1)}s ${msg}`, 'strategy', type);
      sLog(`Tour-Start: session=${sessionName} fastFirst=${new URLSearchParams(location.search).get('fastfirst') !== '0'}`);
      let sendNo = 0;
      guide.eventManager.on('iacp:send-ok', (p: unknown) => {
        const q = (p || {}) as { fastFirst?: boolean; model?: string; elapsedMs?: number; enriched?: boolean; session?: string };
        sendNo++;
        if (q.fastFirst) {
          sLog(`Turn ${sendNo}: FAST-FIRST ok — model=${q.model || '?'} roundtrip=${q.elapsedMs}ms (sync, Pool wärmt parallel)`);
        } else {
          sLog(`Turn ${sendNo}: live-send (Pool ${q.session || '?'}) — send=${q.elapsedMs}ms enriched=${q.enriched}`);
        }
      });
      guide.eventManager.on('iacp:fast-first-fallback', (p: unknown) => {
        sLog(`FAST-FIRST FALLBACK → normales live-send. Grund: ${(p as { error?: string })?.error || '?'}`, 'Warning');
      });
      guide.eventManager.on('iacp:response', (r: unknown) => {
        const t = (r as { topic?: { title?: string } })?.topic?.title || '(ohne Topic)';
        sLog(`Antwort angekommen: „${t}"`);
      });
      guide.eventManager.on('audio-guide:engine-mode', (p: unknown) => {
        const q = (p || {}) as { mode?: string; prev?: string; reason?: string };
        sLog(`Engine: ${q.prev} → ${q.mode} (${q.reason})`);
      });
      guide.eventManager.on('audio-guide:send-skipped', (p: unknown) => {
        const reason = (p as { reason?: string })?.reason || '?';
        if (reason !== 'no-position') sLog(`Send übersprungen: ${reason}`);
      });

      // Pipe the onboarding chip selection into the guide as a multi-
      // select interest list. All slugs land in the FIRST_UPDATE session
      // block as multiple <focus> tags simultaneously, then sticky-
      // suppress on subsequent ticks (the values don't change). The bot
      // sees the full theme set up-front and can weigh them against the
      // current geo-hit — no round-robin per tick.
      const onboardIntents = (window as any).__guideIntents as string[] | undefined;
      if (onboardIntents && onboardIntents.length > 0) {
        guide.setInterests(onboardIntents);
        (window as any).__guideIntents = undefined;
      }

      // Pass user age (derived from stored birthdate) so the bot can
      // adapt tone subtly. Single feature flag → easy to deactivate.
      if (AGE_FEATURE_ENABLED) {
        const bday = getStoredBirthdate();
        const age = bday ? computeAge(bday) : null;
        if (age !== null) guide.setUserAge(age);
      }

      // Realtime-Engine-Mode: den Live-IACP-Send-Loop ab dem allerersten GPS-Fix
      // unterdrücken, damit der Normal-Pool-Pfad NIE parallel narriert. Die
      // Realtime-Session treibt die Narration; GPS-Tracking läuft normal weiter.
      if (isRealtimeMode()) guide.setRealtimeMode(true);

      // Start tracking
      guide.start();
      updateStatus(t('status.guideListening'), t('status.guideListeningDetail'));

      // Onboarding pre-resolved a non-GPS location (surprise / custom city)?
      // → teleport now so the very first GPS update goes out with those
      // coords. Surprise mode is handled inside the lib for subsequent
      // rotations; this call seeds the first one.
      const overridePos = (window as any).__guideOverridePosition as
        { lat: number; lon: number; label: string } | undefined;
      if (overridePos) {
        (window as any).__guideOverridePosition = undefined;
        remoteLog(`Onboarding: seeding manual location → ${overridePos.label}`, 'onboard');
        guide.setManualLocation({
          lat: overridePos.lat, lon: overridePos.lon, accuracy: 50,
          speed: null, heading: null, timestamp: Date.now(),
        });
      }

      // GPS visibility — without this users sit on "Guide hört zu" forever
      // when iOS cellular doesn't deliver a fix or the permission prompt
      // was dismissed. The lib already emits gps:update / gps:error and
      // audio-guide:send-skipped — we just map them to clear UI states.
      // Three layered fallbacks:
      //   t+0   start  → "Guide hört zu… Standort wird ermittelt"
      //   t+8s  no fix → "Warte auf Standort"
      //   t+30s no fix → escalate with "Stadt eingeben"-CTA
      //   GPS error  → immediate denied/unavailable banner with CTA
      // Cancelled once the first iacp:response actually arrives.
      let gpsFixSeen = !!overridePos; // override counts as already located
      let gpsFlowDone = false;
      let pendingUnavailableTimer: number | null = null;
      const cancelGpsFlow = () => {
        gpsFlowDone = true;
        if (pendingUnavailableTimer !== null) {
          window.clearTimeout(pendingUnavailableTimer);
          pendingUnavailableTimer = null;
        }
      };

      const openCustomCityCta = {
        label: t('status.enterCity'),
        onClick: () => window.dispatchEvent(new CustomEvent('wanderlaut:open-custom-city')),
      };

      // Pre-check permission state — denied means no dialog will ever
      // open and we should tell the user immediately.
      try {
        const navAny = navigator as any;
        if (navAny.permissions?.query) {
          const p = await navAny.permissions.query({ name: 'geolocation' });
          if (p?.state === 'denied') {
            updateStatus(
              t('status.locationBlocked'),
              t('status.locationBlockedDetail'),
              openCustomCityCta,
            );
            cancelGpsFlow();
          }
        }
      } catch { /* permissions API not available — fall through */ }

      // Once a fix arrives we drop the wait status. The lib emits gps:update
      // on every fix; first one wins.
      guide.eventManager.on('gps:update', () => {
        if (gpsFixSeen || gpsFlowDone) return;
        gpsFixSeen = true;
        // Cancel any pending "unavailable" banner — a real fix is the
        // best possible rebuttal of an earlier spurious error.
        if (pendingUnavailableTimer !== null) {
          window.clearTimeout(pendingUnavailableTimer);
          pendingUnavailableTimer = null;
        }
        updateStatus(t('status.guideListening'), t('status.guideListeningDetail'));
      });

      // GPS errors that the browser surfaces (code 1=denied, 2=unavailable,
      // 3=timeout). Map them to actionable copy.
      guide.eventManager.on('gps:error', (err: { code?: number; message?: string }) => {
        if (gpsFlowDone) return;
        const code = err?.code ?? 0;
        if (code === 1) {
          // denied is final — no permission grant can come later, show now.
          updateStatus(
            t('status.locationBlocked'),
            t('status.locationBlockedDetail'),
            openCustomCityCta,
          );
          cancelGpsFlow();
        } else if (code === 2) {
          // "unavailable" is fired generously by iOS Safari before the
          // GPS chip has had time to warm up on cellular — usually a real
          // fix lands 1-3s later anyway. Hold the banner for 5s and let
          // a real gps:update cancel it. Without this grace window the
          // user sees "Standort nicht verfügbar" for half a second and
          // then the fix arrives and overwrites it back to "Guide hört
          // zu", which feels glitchy and erodes trust.
          if (pendingUnavailableTimer === null) {
            pendingUnavailableTimer = window.setTimeout(() => {
              pendingUnavailableTimer = null;
              if (gpsFixSeen || gpsFlowDone) return;
              updateStatus(
                t('status.locationUnavailable'),
                t('status.locationUnavailableDetail'),
                openCustomCityCta,
              );
              cancelGpsFlow();
            }, 5000);
          }
        }
        // code 3 (timeout) is handled by our own t+30s escalation below
      });

      // 8 second mid-wait — switch from "Guide hört zu" to explicit wait.
      window.setTimeout(() => {
        if (gpsFixSeen || gpsFlowDone) return;
        updateStatus(t('status.waitingForLocation'), t('status.waitingForLocationDetail'));
      }, 8000);

      // 30 second escalation — offer a way out.
      window.setTimeout(() => {
        if (gpsFixSeen || gpsFlowDone) return;
        gpsFlowDone = true;
        remoteLog('GPS wait 30s elapsed without fix — offering custom-city escape', 'gps', 'Warning');
        updateStatus(
          t('status.noLocation'),
          t('status.noLocationDetail'),
          openCustomCityCta,
        );
      }, 30000);

      // Hide center area only AFTER first response arrives.
      // Also cancel any pending GPS wait warnings — the bot is talking.
      guide.eventManager.on('iacp:response', () => {
        cancelGpsFlow();
        if (centerArea && !centerArea.classList.contains('faded')) {
          centerArea.classList.add('faded');
        }
      });

      // Session state
      sessionState.setSessionName(sessionName);
      sessionState.setAppState('running');

      // Release on tab close. Named handler (not inline arrow) so the
      // dead-session-detect path can removeEventListener before its
      // own explicit release + reload — otherwise reload triggers
      // beforeunload → second releasePool() within ~3s, which appears
      // in cloud-api logs as the "Phantom-Release Pattern" with
      // acquired_by=unknown (Cloud's diagnosis 2026-06-08 Heimstättenstraße).
      onBeforeUnloadRelease = () => releasePool(sessionName);
      if (usingPool) {
        window.addEventListener('beforeunload', onBeforeUnloadRelease);
      }

      // Guide-API session — uses service-to-service auth (X-API-KEY + user_id),
      // NOT browser JWT. Works even if access/refresh tokens are expired.
      if (isLoggedIn()) {
        try {
          guideSessionId = await createSession({
            city: lastCity,
            pool_instance: sessionName,
          });
          if (guideSessionId) {
            remoteLog(`guide-api session: ${guideSessionId}`, 'guide-api');
            // Flush interactions/songs that arrived before session was created
            if (guide) {
              const bus = guide.eventManager;
              // flushPending is defined in wireEvents — call it via a custom event
              bus.emit('guide-api:session-ready');
            }
          } else {
            remoteLog('guide-api: createSession returned null (auth failed?)', 'guide-api', 'Error');
          }
        } catch (e) {
          remoteLog(`guide-api: createSession threw: ${e}`, 'guide-api', 'Error');
        }
      } else {
        remoteLog('guide-api: not logged in — skipping session creation', 'guide-api', 'Warning');
      }

    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      updateStatus(t('status.error'), msg);
      remoteLog(`Start failed: ${msg}`, 'boot', 'Error');
      // Restore welcome state
      if (startBtn) startBtn.style.display = 'inline-flex';
      if (welcomeContent) welcomeContent.style.display = 'block';
      if (statusContent) statusContent.style.display = 'none';
      if (centerArea) centerArea.classList.remove('faded');
    } finally {
      tourStarting = false;
    }
  }

  // START → 2-step onboarding (WHAT, WHERE), then start tour
  const onboardArea = document.getElementById('onboardArea');
  const onboardStep1 = document.getElementById('onboardStep1');
  const onboardStep2 = document.getElementById('onboardStep2');
  const onboardWhatInput = document.getElementById('onboardWhatInput') as HTMLInputElement | null;
  const onboardWhereInput = document.getElementById('onboardWhereInput') as HTMLInputElement | null;

  // Pre-create AudioContext inside a user gesture (iOS requirement).
  // TtsPlayer.primeAudio() picks up window.__preCreatedAudioCtx so that the
  // AudioContext is always created inside a tap handler — even though
  // startTour() is async and breaks the gesture chain.
  let audioCtxPreCreated = false;
  const ensureAudioCtx = () => {
    if (!audioCtxPreCreated) {
      try {
        const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
        ctx.resume().catch(() => {});
        (window as any).__preCreatedAudioCtx = ctx;
        audioCtxPreCreated = true;
        dbg('🔈 AudioCtx pre-created in gesture', '#e0af68');
      } catch {}
    }
  };

  // Pre-select chips that were used last time. Preset chips are matched
  // by data-chip exact-match; intents that don't match a preset get
  // re-created as custom chips. Idempotent — safe to call repeatedly.
  const hydrateChipsFromPrefs = () => {
    const stored = getStoredPrefs().intents;
    if (!stored || stored.length === 0) return;
    const cloud = document.getElementById('onboardChipCloud');
    if (!cloud) return;
    const presets = Array.from(cloud.querySelectorAll<HTMLButtonElement>('.imm-chip'));
    // Translate legacy stored display-texts ("Geschichte und Architektur")
    // to current slugs ("geschichte") on read. Without this, prefs saved
    // before 2026-06-08 would re-render as custom chips even though
    // there's a matching preset.
    const normalizedStored = stored.map(s => LEGACY_CHIP_TEXT_TO_SLUG[s] ?? s);
    let migrated = false;
    for (let i = 0; i < stored.length; i++) {
      if (normalizedStored[i] !== stored[i]) migrated = true;
    }
    if (migrated) {
      setStoredPrefs({ intents: normalizedStored });
    }
    for (const intent of normalizedStored) {
      const existing = presets.find(c => (c.dataset.chip || '') === intent);
      if (existing) {
        existing.classList.add('selected');
      } else {
        // Recreate custom chip
        const chip = document.createElement('button');
        chip.className = 'imm-chip selected';
        chip.dataset.chip = intent;
        chip.dataset.custom = '1';
        chip.innerHTML = `<i data-lucide="tag"></i><span>${intent.replace(/[<>]/g, '')}</span>`;
        chip.addEventListener('click', () => {
          if (chip.dataset.custom === '1' && !chip.classList.contains('selected')) {
            chip.remove();
            return;
          }
          chip.classList.toggle('selected');
        });
        cloud.appendChild(chip);
        if ((window as any).lucide) (window as any).lucide.createIcons({ nodes: [chip] });
      }
    }
  };

  // Pre-select the right transport button in Step 2 based on prefs.
  const hydrateTransportFromPrefs = () => {
    const t = pendingTransport;
    document.querySelectorAll<HTMLButtonElement>('.imm-transport-btn[data-transport]')
      .forEach(b => b.classList.toggle('selected', b.dataset.transport === t));
  };

  startBtn?.addEventListener('click', () => {
    ensureAudioCtx();
    // Hide welcome content, show onboarding step 1
    const wc = document.getElementById('welcomeContent');
    if (wc) wc.style.display = 'none';
    if (startBtn) startBtn.style.display = 'none';
    if (onboardArea) onboardArea.style.display = 'flex';
    if (onboardStep1) onboardStep1.style.display = 'flex';
    if (onboardStep2) onboardStep2.style.display = 'none';
    hydrateChipsFromPrefs();
    hydrateTransportFromPrefs();
    // No auto-focus on the custom-category input. iOS Safari zooms in
    // and pops the keyboard on every focus() call, which is jarring
    // when the user just wants to tap the preset chips. Focus happens
    // later only on explicit user intent (tap "+" or hit Enter).
  });

  // Shortcut from the GPS-denied status banner: jump straight to Step 2
  // with the custom-city input pre-selected and focused. Skips the
  // category-cloud step entirely — when GPS is dead, "show me this
  // city" is the primary intent.
  // Realtime-Engine (?realtime=1) braucht ein Login-JWT. Ohne Login wäre die App
  // sonst STUMM (Pool geskippt + Send-Loop suppressed, Session nie gestartet) —
  // also persistenter Status-Banner mit Login-CTA statt eines einmaligen alert().
  window.addEventListener('wanderlaut:realtime-login-required', () => {
    const sc = document.getElementById('statusContent');
    if (sc) sc.style.display = 'block';
    updateStatus(
      'Live-Guide braucht einen Login',
      'Der schnelle Realtime-Guide funktioniert nur angemeldet — bitte zuerst einloggen, dann neu starten.',
      { label: 'Mit Google anmelden', onClick: () => void startGoogleLogin() },
    );
  });

  window.addEventListener('wanderlaut:open-custom-city', () => {
    ensureAudioCtx();
    const wc = document.getElementById('welcomeContent');
    if (wc) wc.style.display = 'none';
    const sc = document.getElementById('statusContent');
    if (sc) sc.style.display = 'none';
    if (startBtn) startBtn.style.display = 'none';
    if (onboardArea) onboardArea.style.display = 'flex';
    if (onboardStep1) onboardStep1.style.display = 'none';
    if (onboardStep2) onboardStep2.style.display = 'flex';
    hydrateChipsFromPrefs();
    hydrateTransportFromPrefs();
    selectWhere('custom');
  });

  /** Resolve a non-GPS location BEFORE startTour runs the warmup cache
   *  lookup, so the preview entry comes from the chosen area rather than
   *  the user's real GPS. Returns null on failure → caller falls back. */
  // Test-Overrides — lookup table of known place names that should
  // land on a specific GPS spot instead of whatever Nominatim's
  // geocoder returns as the geographic centroid. Useful for QA
  // fixtures where we want a specific Knowledgepoint / POI to be
  // in-range without the user having to think about coordinates.
  // Key is case-insensitive exact match on the trimmed input.
  // Remove when no longer needed for testing.
  const TEST_LOCATION_OVERRIDES: Record<string, { lat: number; lon: number; label: string }> = {
    'tscheppaschlucht': {
      // Rotbuche-Knowledgepoint Position auf Route 56 (Tscheppaschlucht).
      // WP #24912 Rotbuche, km 1.876, 1.7m lateral offset zur Polyline,
      // knowledge_id=283, has_3d_model=true → Renderer3D aktiviert sich
      // automatisch im Frontend (KnowledgeView.ts:283 prüft media[].kind).
      // E2E-Test-Position für volle Pipeline: artrack track_detect (30) →
      // context-at injection → bot baut Topic-Card mit Rotbuche +
      // GLB-Modell → 3D-Render im Frontend.
      lat: 46.48693, lon: 14.27320,
      label: 'Tscheppaschlucht (Rotbuche-Knowledgepoint #24912 — Route 56 km 1.876, 3D-ready)',
    },
  };

  const resolveOverridePosition = async (
    where: 'surprise' | string,
  ): Promise<{ lat: number; lon: number; label: string } | null> => {
    if (where === 'surprise') {
      const c = await getRandomCity(1000);
      if (!c) return null;
      return { lat: c.lat, lon: c.lon, label: `${c.name}, ${c.country_code}` };
    }
    // Test-override lookup before hitting the geocoder.
    const overrideKey = where.trim().toLowerCase();
    const override = TEST_LOCATION_OVERRIDES[overrideKey];
    if (override) {
      remoteLog(`Test-override: "${where}" → ${override.lat},${override.lon}`, 'gps');
      return override;
    }
    // Specific place name → forward geocode via Nominatim
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(where)}&format=json&limit=1`);
      const data = await r.json();
      if (Array.isArray(data) && data.length > 0) {
        return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon), label: where };
      }
    } catch { /* network/timeout */ }
    return null;
  };

  /** Final start — intent string + location choice ('here' | 'surprise' | a place name).
   *  For non-'here' targets we await coordinate resolution BEFORE handing off
   *  to startTour, so the warmup cache pick uses the chosen area.
   *  Also persists the user's current preferences (intents/transport/etc)
   *  so the next visit starts pre-populated. */
  const doStart = async (intents: string[], where: 'here' | 'surprise' | string) => {
    ensureAudioCtx();
    if (intents.length > 0) (window as any).__guideIntents = intents;

    // Persist preferences for next visit. customLocation IS stored
    // because a session-reset (dead-session 240s timeout → reload)
    // shouldn't silently drop the user's "Tscheppaschlucht" pick and
    // wake up at live-GPS. Reset is rare and almost never wanted;
    // when it happens, restoring intent is the friendly default. The
    // user can always pick "Hier" or "Überraschung" next visit to
    // clear it (we overwrite below in those branches).
    const customLocStr = (where !== 'here' && where !== 'surprise')
      ? String(where)
      : '';
    setStoredPrefs({
      intents,
      transport: pendingTransport,
      language: pendingLang,
      mode: pendingMode,
      customLocation: customLocStr,
    });

    if (where !== 'here') {
      // Show a brief loading state while we resolve coords. The onboarding
      // overlay stays up so the welcome content doesn't flash through.
      updateStatus(t('status.computingLocation'),
        where === 'surprise' ? 'Würfel einen zufälligen Ort.' : `Suche "${where}".`);
      const resolved = await resolveOverridePosition(where);
      if (!resolved) {
        showToast(where === 'surprise'
          ? 'Konnte keinen Zufallsort holen — Live-GPS wird verwendet.'
          : `Ort "${where}" nicht gefunden — Live-GPS wird verwendet.`);
        // Fall through with no override → real GPS path
      } else {
        // Hand the pre-resolved coords to startTour so the warmup uses them
        (window as any).__guideOverridePosition = {
          lat: resolved.lat, lon: resolved.lon, label: resolved.label,
        };
        if (where === 'surprise') pendingMode = 'surprise';
        remoteLog(`Onboarding: override position → ${resolved.label} (${resolved.lat.toFixed(3)}, ${resolved.lon.toFixed(3)})`, 'onboard');
      }
    }

    if (onboardArea) onboardArea.style.display = 'none';
    void startTour();
  };

  // Step 1 → 2 transition
  const goToStep2 = () => {
    if (onboardStep1) onboardStep1.style.display = 'none';
    if (onboardStep2) onboardStep2.style.display = 'flex';
    onboardWhereInput?.focus();
  };

  // Chip buttons — multi-select toggle. Custom chips can be removed by
  // tapping a selected one twice (first tap toggles selected off, second
  // tap removes the chip entirely — only for user-added chips).
  const cloudEl = document.getElementById('onboardChipCloud');
  const toggleChip = (chip: HTMLButtonElement) => {
    if (chip.dataset.custom === '1' && !chip.classList.contains('selected')) {
      // An already-deselected custom chip: tapping it again removes it
      chip.remove();
      return;
    }
    chip.classList.toggle('selected');
  };
  document.querySelectorAll<HTMLButtonElement>('.imm-chip').forEach(chip => {
    chip.addEventListener('click', () => toggleChip(chip));
  });

  // Custom chip: tap "+" or hit Enter in the input → adds a new chip
  // already in selected state. Empty input is ignored.
  const addCustomChip = () => {
    const text = onboardWhatInput?.value?.trim();
    if (!text || !cloudEl) return;
    // De-dupe: if an existing preset matches, just select it
    const existing = Array.from(cloudEl.querySelectorAll<HTMLButtonElement>('.imm-chip'))
      .find(c => (c.dataset.chip || '').toLowerCase() === text.toLowerCase());
    if (existing) {
      existing.classList.add('selected');
    } else {
      const chip = document.createElement('button');
      chip.className = 'imm-chip selected';
      chip.dataset.chip = text;
      chip.dataset.custom = '1';
      chip.innerHTML = `<i data-lucide="tag"></i><span>${text.replace(/[<>]/g, '')}</span>`;
      chip.addEventListener('click', () => toggleChip(chip));
      cloudEl.appendChild(chip);
      if ((window as any).lucide) (window as any).lucide.createIcons({ nodes: [chip] });
    }
    if (onboardWhatInput) onboardWhatInput.value = '';
  };
  // Tapping "+" with an empty input should pop the keyboard (so users
  // don't have to tap the input first). After adding a chip we re-focus
  // the input so they can quickly add another. iOS Safari only accepts
  // .focus() inside a user-gesture handler — this is one.
  document.getElementById('onboardAddChipBtn')?.addEventListener('click', () => {
    addCustomChip();
    onboardWhatInput?.focus();
  });
  onboardWhatInput?.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); addCustomChip(); onboardWhatInput?.focus(); }
  });

  /**
   * One-time migration: localStorage entries from before 2026-06-08
   * stored chip display-text ("Geschichte und Architektur") instead
   * of the focus-slug ("geschichte"). Now that the template's data-chip
   * attributes are slugs, those old entries would re-render as
   * "custom" chips. Translate them on read.
   */
  const LEGACY_CHIP_TEXT_TO_SLUG: Record<string, string> = {
    'Geschichte und Architektur': 'geschichte',
    'Restaurants, Cafés, gute Lokale': 'kulinarik',
    'Geologie und Natur': 'geologie',
    'Bars, Nightlife, Live-Spots': 'nachtleben',
    'Handwerk und Manufaktur': 'kultur',
    'Musik, Konzerte, lokale Musikszene': 'musik',
    'Kunst und Museen': 'kunst',
    'Sport, Wandern, Outdoor-Aktivitäten': 'outdoor',
  };

  /** Collect selected intents from chip cloud. data-chip is already the
   *  focus-slug for presets, or user-typed text for custom chips. */
  const collectSelectedIntents = (): string[] =>
    Array.from(document.querySelectorAll<HTMLButtonElement>('.imm-chip.selected'))
      .map(c => (c.dataset.chip || '').trim())
      .filter(Boolean);

  // Step 1 controls
  document.getElementById('onboardWhatNext')?.addEventListener('click', goToStep2);
  document.getElementById('onboardSkipWhat')?.addEventListener('click', () => {
    // Skip = clear all selections and continue
    document.querySelectorAll('.imm-chip').forEach(c => c.classList.remove('selected'));
    goToStep2();
  });

  // Step 2 controls — back to step 1
  document.getElementById('onboardBack')?.addEventListener('click', () => {
    if (onboardStep2) onboardStep2.style.display = 'none';
    if (onboardStep1) onboardStep1.style.display = 'flex';
  });

  // Step 2 — radio selection between three location options.
  // Default-selected is 'here' (set via .selected in template). Tapping
  // the third option ('custom') reveals the inline text input.
  // The transport pre-selector above only makes physical sense for
  // "here" (real GPS) — for surprise/custom we hide it.
  const whereBtns = document.querySelectorAll<HTMLButtonElement>('.imm-onboard-bigbtn[data-where]');
  const transportRow = document.getElementById('onboardTransport');
  let selectedWhere: 'here' | 'surprise' | 'custom' = 'here';
  const selectWhere = (mode: 'here' | 'surprise' | 'custom') => {
    selectedWhere = mode;
    whereBtns.forEach(b => b.classList.toggle('selected', b.dataset.where === mode));
    if (onboardWhereInput) {
      onboardWhereInput.style.display = (mode === 'custom') ? 'block' : 'none';
      if (mode === 'custom') onboardWhereInput.focus();
    }
    transportRow?.classList.toggle('hidden', mode !== 'here');
  };
  whereBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      selectWhere(btn.dataset.where as 'here' | 'surprise' | 'custom');
    });
  });

  // Restore last "Konkreter Ort" pick if the user had one. Reset (dead
  // session 240s timeout) would otherwise wipe their Tscheppaschlucht
  // choice and silently fall back to live-GPS — friendlier to restore
  // intent and let them tap "Hier" / "Überraschung" to clear.
  if (storedPrefs.customLocation && onboardWhereInput) {
    onboardWhereInput.value = storedPrefs.customLocation;
    selectWhere('custom');
  }

  // Transport pre-selector — radio between walking/bike/car. Updates
  // pendingTransport so startTour() picks it up. Walking is selected by
  // default via .selected in the template.
  const transportBtns = document.querySelectorAll<HTMLButtonElement>('.imm-transport-btn[data-transport]');
  transportBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const t = btn.dataset.transport;
      if (!t) return;
      pendingTransport = t;
      transportBtns.forEach(b => b.classList.toggle('selected', b === btn));
      if (guide) guide.setTransport(t as any);
    });
  });

  // Primary "Los geht's" — uses the currently-selected option.
  // Shows a spinner inside the button while doStart resolves coords /
  // acquires a pool session, then resets if the flow falls back to
  // welcome (e.g. failed geocode + GPS-less, errored pool acquire).
  const startGoBtn = document.getElementById('onboardWhereGo');
  const startGoOriginalHtml = startGoBtn?.innerHTML ?? '';
  const setStartLoading = (loading: boolean) => {
    if (!startGoBtn) return;
    if (loading) {
      startGoBtn.classList.add('is-loading');
      startGoBtn.setAttribute('aria-busy', 'true');
      startGoBtn.innerHTML = `<span class="imm-spinner"></span><span>${t('common.preparing')}</span>`;
    } else {
      startGoBtn.classList.remove('is-loading');
      startGoBtn.removeAttribute('aria-busy');
      startGoBtn.innerHTML = startGoOriginalHtml;
      if ((window as any).lucide) (window as any).lucide.createIcons({ nodes: [startGoBtn] });
    }
  };
  const submitWhere = async () => {
    if (selectedWhere === 'custom') {
      const place = onboardWhereInput?.value?.trim();
      if (!place) {
        // No place entered → keep user on screen with the input focused
        onboardWhereInput?.focus();
        return;
      }
      setStartLoading(true);
      try { await doStart(collectSelectedIntents(), place); }
      finally { setStartLoading(false); }
    } else {
      setStartLoading(true);
      try { await doStart(collectSelectedIntents(), selectedWhere); }
      finally { setStartLoading(false); }
    }
  };
  startGoBtn?.addEventListener('click', () => { void submitWhere(); });
  onboardWhereInput?.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); void submitWhere(); }
  });

  // ── Step 4: Wire guide events (called after guide init) ──
  function wireEvents(g: AudioGuide, session: string): void {
    const bus = g.eventManager;

    // Debug: log all key events + update status pills
    setStatus('pool', `Pool: ${session}`, 'ok');

    bus.on('tracking:changed', (s: any) => {
      dbg(`tracking: running=${s.isRunning}`);
      setStatus('track', s.isRunning ? 'GPS ON' : 'GPS OFF', s.isRunning ? 'ok' : 'wait');
    });
    // GPS pipeline timing — wire send timestamp at log:message ("→ Sent")
    // and close the loop at iacp:response. Single-flight assumption: the
    // tracker sends one GPS update at a time and waits for its reply
    // before the next tick, so "the last send" maps unambiguously to
    // "the next response". msgId is parsed out of the log text for
    // confirmation in the debug line.
    let lastGpsSentAt: number | null = null;
    let lastGpsMsgId: string | null = null;
    bus.on('iacp:response', (r: any) => {
      const preview = (r?.content || '').slice(0, 60);
      if (lastGpsSentAt !== null) {
        const ms = Date.now() - lastGpsSentAt;
        const s = (ms / 1000).toFixed(1);
        dbg(`← IACP reply in ${s}s (#${lastGpsMsgId || '?'}): "${preview}..."`, '#7aa2f7');
        lastGpsSentAt = null;
        lastGpsMsgId = null;
      } else {
        dbg(`← IACP: "${preview}..."`, '#7aa2f7');
      }
      setStatus('iacp', 'IACP OK', 'ok');
    });
    // Stream-Recovery events from IacpClient — fired when bfcache /
    // long-hidden visibility forces a reconnect of in-flight SSE.
    // Surface them so we can correlate "session pending" symptoms with
    // OS-suspend events in real-world tests.
    bus.on('iacp:stream-recovery', (p: any) => {
      const msg = `↻ SSE recovery: ${p?.count || '?'} stream(s), reason=${p?.reason || '?'}`;
      dbg(msg, '#e0af68');
      remoteLog(msg, 'iacp');
    });
    // Queue POST result events — added 2026-06-08 after the queue_worker
    // hang debacle where the symptom (no response) was visible but the
    // cause (worker dead) was invisible from FE. Now every send logs its
    // HTTP outcome to log-api so we can pre-empt support questions.
    bus.on('iacp:send-ok', (p: any) => {
      remoteLog(`→ enqueued msgId=${p?.msgId} in ${p?.elapsedMs}ms`, 'iacp');
    });
    bus.on('iacp:send-error', (p: any) => {
      const detail = p?.phase === 'http'
        ? `HTTP ${p?.status} body="${(p?.body || '').slice(0, 100)}"`
        : `network error: ${p?.error || '?'}`;
      const msg = `✗ enqueue FAILED after ${p?.elapsedMs}ms (${p?.phase}): ${detail}`;
      dbg(msg, '#f7768e');
      remoteLog(msg, 'iacp', 'Error');
    });
    // Pending-too-long warnings — fires at 60s (slow) and 120s (likely stuck).
    // Pre-fix these were silent: the 240s pollResponse timeout would just
    // tick over without any indication. Now the FE log shows the stuck
    // state at 60s already, which is the actual visibility we want.
    bus.on('iacp:pending-warning', (p: any) => {
      const secs = ((p?.elapsedMs || 0) / 1000).toFixed(0);
      const sev = p?.severity === 'stuck' ? 'Error' : 'Warning';
      const msg = `⚠ msgId=${p?.msgId} pending ${secs}s (${p?.severity}) — backend or queue worker may be stuck`;
      dbg(msg, p?.severity === 'stuck' ? '#f7768e' : '#e0af68');
      remoteLog(msg, 'iacp', sev);
    });
    bus.on('tts:primed', (p: any) => {
      dbg(`AudioCtx: reused=${p?.reused} state=${p?.state} rate=${p?.sampleRate}`, '#e0af68');
      setStatus('audio', `Ctx: ${p?.state}`, p?.state === 'running' ? 'ok' : 'warn');
    });
    bus.on('tts:fetch-start', (p: any) => {
      dbg(`TTS fetch: "${p?.label}" ctx=${p?.ctxState}`, '#565f89');
      setStatus('tts', 'TTS...', 'wait');
    });
    bus.on('tts:fetch-done', (p: any) => {
      dbg(`TTS fetched: ${((p?.bytes || 0) / 1024).toFixed(0)}KB`, '#565f89');
      const kb = (p?.bytes || 0) / 1024;
      setStatus('tts', `TTS ${kb.toFixed(0)}KB`, kb > 1 ? 'ok' : 'err');
    });
    bus.on('tts:start', (p: any) => {
      const ctx = p?.ctxState || '?';
      dbg(`TTS play: ${p?.label || '?'} (ctx=${ctx})`);
      setStatus('tts', 'TTS PLAY', ctx === 'running' ? 'ok' : 'warn');
    });
    bus.on('tts:end', () => { dbg('TTS end'); setStatus('tts', 'TTS DONE', 'ok'); });
    bus.on('tts:error', (e: any) => { dbg(`TTS error: ${e?.message} ctx=${e?.ctxState || '?'}`, '#f7768e'); setStatus('tts', 'TTS ERR', 'err'); });
    bus.on('music:started', (p: any) => { dbg(`Music: ${p?.title || '?'}`); setStatus('music', 'Music ON', 'ok'); });
    bus.on('music:load-failed', (p: any) => { dbg(`Music fail: ${p?.error || '?'}`, '#f7768e'); setStatus('music', 'Music ERR', 'err'); });
    // F3 debug overlay — keep last songs snapshot for instant re-render on toggle
    bus.on('songs:changed', (snap: any) => {
      lastSongsSnap = snap;
      renderMusicDebugOverlay();
    });
    bus.on('topic:changed', (p: any) => {
      const t = p?.topic;
      if (!t) { dbg(`Topic: null`); return; }
      // Compact one-liner: title, what visuals are available, geo anchor.
      // The image/wiki state explains "black topic card" symptoms — if
      // both are missing, the renderer has nothing to put behind the
      // text. wikipedia=yes + image=no means the WikipediaEnricher is
      // expected to lazy-load the image in a follow-up event.
      const flags: string[] = [];
      flags.push(t.image ? 'img✓' : 'img✗');
      flags.push(t.wikipedia ? 'wiki✓' : 'wiki✗');
      if (t.lat != null && t.lon != null) flags.push(`geo=${t.lat.toFixed(3)},${t.lon.toFixed(3)}`);
      dbg(`🖼 Topic: ${t.title || '(no title)'} [${flags.join(' ')}]`, '#bb9af7');
    });
    bus.on('log:message', (e: any) => {
      if (e?.text?.includes('→ Sent')) {
        // Stamp the send timestamp + extract msgId for the next reply's
        // latency line. Format from AudioGuide: "→ Sent #N (msgId=abc123)".
        const m = e.text.match(/msgId=([\w-]+)/);
        lastGpsMsgId = m ? m[1] : null;
        lastGpsSentAt = Date.now();
        dbg(`→ GPS sent (#${lastGpsMsgId || '?'})`, '#565f89');
        setStatus('iacp', 'Waiting…', 'wait');
      }
    });

    // Audio health: show "tap to fix" banner when AudioContext is blocked
    bus.on('tts:audio-blocked', (p: any) => {
      dbg(`AUDIO BLOCKED: ctx=${p?.state}`, '#f7768e');
      setStatus('audio', 'STUMM', 'err');
      remoteLog(`Audio blocked: ctx=${p?.state}`, 'audio', 'Error');
      // Show a tap-to-fix banner
      let banner = document.getElementById('audioFixBanner');
      if (!banner) {
        banner = document.createElement('div');
        banner.id = 'audioFixBanner';
        banner.style.cssText = 'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);z-index:9999;background:rgba(247,118,142,0.95);color:#fff;padding:16px 24px;border-radius:16px;font-size:15px;font-weight:700;font-family:system-ui;text-align:center;cursor:pointer;backdrop-filter:blur(8px)';
        banner.textContent = t('toast.muted');
        banner.addEventListener('click', () => {
          // User gesture → resume AudioContext
          g.primeAudio();
          banner!.remove();
          setStatus('audio', 'Ctx: fixed', 'ok');
          dbg('Audio: user tap → primeAudio()', '#9ece6a');
        });
        document.body.appendChild(banner);
        // Auto-remove after 10s
        setTimeout(() => banner?.remove(), 10000);
      }
    });

    bus.on('user:stop', () => {
      g.stop();
      if (usingPool) {
        // Symmetric Phantom-Release-Prevention: explicit stop releases,
        // so any later beforeunload (tab close after stop) should NOT
        // release again — slot might've been picked up by another
        // acquirer in between.
        if (onBeforeUnloadRelease) {
          window.removeEventListener('beforeunload', onBeforeUnloadRelease);
        }
        releasePool(session);
      }
      sessionState.clear();
      remoteLog(`Pool released: ${session}`, 'pool');
      // End guide-api session
      if (guideSessionId) {
        void endSession(guideSessionId);
        guideSessionId = null;
      }
      // Restore welcome screen buttons
      if (isLoggedIn()) {
        if (startBtn) startBtn.style.display = 'inline-flex';
        const user = getUser();
        if (userGreeting && user) {
          userGreeting.style.display = 'block';
          userGreeting.textContent = t('welcome.greeting', { name: user.first_name });
        }
      } else {
        if (signinBtn) signinBtn.style.display = 'inline-flex';
      }
    });

    // Mode / Transport / Interval
    bus.on('user:mode-change', (p: { mode: RideMode }) => g.setMode(p.mode));
    bus.on('user:transport-change', (p: { transport: TransportMode }) => g.setTransport(p.transport));
    bus.on('user:interval-change', (p: { seconds: number }) => g.setUpdateInterval(p.seconds));
    bus.on('user:drift-request', () => { void g.requestSuggestion(); });
    bus.on('user:more-like-this', () => { void g.moreLikeThis(); });
    bus.on('user:tts-pause', () => g.pauseNarration());
    bus.on('user:reboot-guide', () => {
      const sel = document.getElementById('modelSelect') as HTMLSelectElement | null;
      const model = (sel?.value === 'opus' ? 'opus' : 'sonnet') as 'sonnet' | 'opus';
      void g.resetSession(model);
    });
    bus.on('user:yt-next', () => g.skipCurrentSong());
    bus.on('user:special-track-change', (p: { trackId: number | null }) => g.setTrack(p.trackId));
    bus.on('user:narrator-change', (p: { name: string }) => g.setNarrator(p.name));
    bus.on('user:interest-toggle', (p: { slug: string }) => g.toggleInterest(p.slug));
    bus.on('user:osm-toggle', (p: { enabled: boolean }) => g.setOsmContext(p.enabled));
    bus.on('user:osm-radius', (p: { radius: number }) => g.setOsmRadius(p.radius));

    // Language buttons are wired in bootstrap (before guide exists)

    // Music toggle
    bus.on('user:music-off', () => { g.musicPlayer.pause(); });
    bus.on('user:music-on', () => { g.musicPlayer.resume(); });
    bus.on('user:music-skip', () => { g.musicPlayer.skip(); });

    // Mic toggle. Recording silences TTS + music so the user's voice is
    // not contaminated by the bot's reply or background ambient music.
    // Errors (permission denied, no mic) are surfaced via remoteLog +
    // a visible toast so the user knows WHY the button looks dead.
    /** Logical recording intent. Differs from UI state because iOS Safari
     *  shows a permission prompt that blocks getUserMedia for an
     *  indeterminate time — UI must not jump to "recording" until the lib
     *  emits voice:start (= MediaRecorder actually started). */
    let voiceRecording = false;
    let musicWasPlayingPreRec = false;
    /** Mic button quad-state. */
    const setMicBtnState = (state: 'idle' | 'preparing' | 'recording' | 'processing') => {
      const btn = document.getElementById('micBtn');
      if (!btn) return;
      btn.classList.toggle('preparing', state === 'preparing');
      btn.classList.toggle('recording', state === 'recording');
      btn.classList.toggle('processing', state === 'processing');
      // Mirror the recording state onto the tour-controls bar so the
      // CSS can hide the tour-stop button (which sits visually next to
      // the mic and looks like "stop recording" to users). Done here so
      // every state-change path is covered, not just the toggle handler.
      const bar = document.getElementById('tourControls');
      if (bar) bar.classList.toggle('recording-active', state === 'recording' || state === 'preparing');
    };

    // While recording is live, ANY incoming TTS / music start has to be
    // muted immediately — otherwise iOS picks the speaker output back
    // up through the microphone and the user's question gets garbled.
    // The mic-toggle start path already pauses both sources; this
    // guard catches the case where a new bot reply arrives DURING the
    // recording and would otherwise auto-play.
    bus.on('tts:start', () => {
      if (voiceRecording) {
        try { g.pauseSpeech(); } catch { /* ignore */ }
        remoteLog('Mic: re-paused TTS that started mid-recording', 'voice');
      }
    });
    bus.on('music:started', () => {
      if (voiceRecording) {
        try { g.musicPlayer.pause(); } catch { /* ignore */ }
        remoteLog('Mic: re-paused music that started mid-recording', 'voice');
      }
    });

    // Sync UI to actual recorder lifecycle (not the tap-handler optimism)
    bus.on('voice:start', () => {
      remoteLog('Mic: voice:start (recording really running)', 'voice');
      if (!voiceRecording) return;
      setMicBtnState('recording');
      // Defensive re-pause: iOS Safari occasionally resumes audio playback
      // the moment the microphone stream becomes active (OS audio-session
      // bookkeeping). Without this, the user can find music coming through
      // the mic during recording. Also re-pause TTS in case the breather
      // timer was about to start a new package while the mic was acquiring.
      try { g.pauseSpeech(); } catch { /* ignore */ }
      if (g.musicPlayer.isPlaying) {
        try { g.musicPlayer.pause(); } catch { /* ignore */ }
      }
    });
    bus.on('voice:error', (payload: any) => {
      remoteLog(`Mic: voice:error ${payload?.message || ''}`, 'voice', 'Error');
      voiceRecording = false;
      setMicBtnState('idle');
    });

    /** Clear processing state once the bot's next response arrives —
     *  hooked once per session, no-op after first fire. */
    let micProcessingActive = false;
    bus.on('iacp:response', () => {
      if (micProcessingActive) {
        micProcessingActive = false;
        setMicBtnState('idle');
      }
    });
    bus.on('user:mic-toggle', () => {
      if (!voiceRecording) {
        // Start: pause audio sources, then begin recording.
        try { g.pauseSpeech(); } catch { /* ignore */ }
        musicWasPlayingPreRec = g.musicPlayer.isPlaying;
        if (musicWasPlayingPreRec) {
          try { g.musicPlayer.pause(); } catch { /* ignore */ }
        }
        remoteLog('Mic: requesting permission + starting (TTS+music paused)', 'voice');
        voiceRecording = true;
        // 'preparing' visually communicates that we're WAITING for the
        // browser permission prompt / device init. Only when the lib emits
        // voice:start do we flip to the red pulsing 'recording' state.
        // Without this, iOS Safari users tap mic, see the red pulse, but
        // the permission dialog is still open — they think it's recording
        // when nothing's happening yet.
        setMicBtnState('preparing');
        g.voiceRecorder.start().catch(err => {
          voiceRecording = false;
          setMicBtnState('idle');
          const msg = err instanceof Error ? err.message : String(err);
          remoteLog(`Mic start FAILED: ${msg}`, 'voice', 'Error');
          dbg(`Mic start failed: ${msg}`, '#f7768e');
          const isPermission = /permission|denied|notallowed/i.test(msg);
          const text = isPermission
            ? 'Mikrofon-Zugriff verweigert. Bitte in den Browser-Einstellungen erlauben.'
            : `Aufnahme fehlgeschlagen: ${msg}`;
          showToast(text);
          if (musicWasPlayingPreRec) {
            try { g.musicPlayer.resume(); } catch { /* ignore */ }
          }
        });
      } else {
        voiceRecording = false;
        remoteLog('Mic: stopping recording', 'voice');
        // Switch button to spinner — visible feedback that audio is being
        // transcribed + Guide is composing a reply. Cleared by the next
        // iacp:response (handler above).
        setMicBtnState('processing');
        micProcessingActive = true;
        // Music resumes immediately — the wait is up to ~30s, the user
        // shouldn't sit in silence. The Guide's new reply, when it arrives,
        // will queue fresh TTS that takes over playback.
        if (musicWasPlayingPreRec) {
          try { g.musicPlayer.resume(); } catch { /* ignore */ }
        }
        g.voiceRecorder.stop().then((blob) => {
          if (blob) {
            remoteLog(`Mic: blob ${blob.size}B, sending to guide`, 'voice');
            void g.askVoiceQuestion(blob);
          } else {
            remoteLog('Mic: stop returned no blob', 'voice', 'Warning');
            // No blob → no reply will come → manually clear processing state
            micProcessingActive = false;
            setMicBtnState('idle');
          }
        }).catch(err => {
          remoteLog(`Mic stop FAILED: ${err instanceof Error ? err.message : err}`, 'voice', 'Error');
          micProcessingActive = false;
          setMicBtnState('idle');
        });
      }
    });

    // Photo
    const photoInput = document.getElementById('photoInput') as HTMLInputElement | null;
    const photoBtn = document.getElementById('photoBtn');
    photoBtn?.addEventListener('click', () => {
      remoteLog('📷 photoBtn clicked → opening file picker', 'photo');
      photoInput?.click();
    });
    photoInput?.addEventListener('change', () => {
      const file = photoInput.files?.[0];
      if (file) {
        remoteLog(`📷 photoInput change: file ${file.name} (${(file.size/1024).toFixed(0)}KB, ${file.type})`, 'photo');
        void g.askPhotoQuestion(file);
        photoInput.value = '';
      } else {
        remoteLog('📷 photoInput change fired but no file selected', 'photo', 'Warning');
      }
    });

    // Session state persistence
    bus.on('topic:changed', (payload: { topic: any }) => {
      if (payload.topic) {
        sessionState.setLastTopic({
          title: payload.topic.title || '',
          subtitle: payload.topic.subtitle || '',
          image: payload.topic.image || null,
          wikipedia: payload.topic.wikipedia || null,
          category: (payload.topic.tags?.[0] || '').replace(/^[^\w]*/, ''),
          location: lastCity,
        });
        // Track the active topic title so tts:end knows which interaction
        // row the freshly-played MP3 belongs to. Always update — fires
        // again on enrichment merge with same title (idempotent).
        const title = payload.topic.title || '';
        if (title) currentActiveTopicTitle = title;
        // Attach image to the interaction THIS topic belongs to — looked
        // up by title because lastInteractionId tracks the most recent log,
        // not the currently-playing one (matters when topics #2/#3 advance).
        if (payload.topic.image && title && isLoggedIn()) {
          const targetId = topicToInteractionId.get(title) ?? lastInteractionId;
          if (targetId) {
            void attachImage(targetId, payload.topic.image)
              .then(ok => { if (ok) remoteLog(`guide-api: image attached to ${targetId} (${title})`, 'guide-api'); });
          }
        }
      }
    });
    bus.on('iacp:response', (res: any) => {
      if (res?.content) sessionState.setLastResponse(res.content);
    });
    bus.on('location:changed', (loc: any) => {
      if (loc?.lat != null) { lastLat = loc.lat; sessionState.setLastPosition(loc.lat, loc.lon); }
      if (loc?.lon != null) lastLon = loc.lon;
      if (loc?.city) lastCity = loc.city;
    });

    // Guide-API logging + TTS archival (authenticated only)
    const pendingInteractions: any[] = [];
    const pendingSongs: any[] = [];
    let lastInteractionId: number | null = null;
    // Map topic title → interaction ID, so audio/image attaches on
    // sequential package activation hit the correct DB row. Each
    // iacp:response logs main + 2 additional_topics — three IDs land here.
    const topicToInteractionId = new Map<string, number>();
    // Title of the currently-active package — updated by topic:changed.
    // tts:end uses this to know which interaction the MP3 belongs to.
    let currentActiveTopicTitle: string = '';

    const flushPending = () => {
      if (!guideSessionId) return;
      for (const p of pendingInteractions.splice(0)) {
        const title = p._topicTitle || p.topic_title || '';
        const payload = { ...p, session_id: guideSessionId };
        delete (payload as any)._topicTitle;
        void logInteraction(payload)
          .then(id => {
            if (id) {
              lastInteractionId = id;
              if (title) topicToInteractionId.set(title, id);
              remoteLog(`guide-api: interaction ${id} logged`, 'guide-api');
            }
          })
          .catch(e => remoteLog(`guide-api: logInteraction failed: ${e}`, 'guide-api', 'Error'));
      }
      for (const p of pendingSongs.splice(0)) {
        void logSong({ ...p, session_id: guideSessionId })
          .catch(e => remoteLog(`guide-api: logSong failed: ${e}`, 'guide-api', 'Error'));
      }
    };

    if (isLoggedIn()) {
      // Helper: log one interaction and (when not buffered) populate the
      // title→ID map so audio/image attaches can find it later.
      const logOne = (interaction: any, title: string) => {
        // World-Korpus Phase 1: generische Narration (kein aktiver Track) →
        // world_corpus=true. guide-api spiegelt sie als narration_point auf den
        // Welt-Track (mit Radius-Dedupe). Nur Inhalt, keine GPS-Telemetrie.
        if (WORLD_CORPUS_ENABLED && guide && guide.currentTrackId == null) {
          interaction = { ...interaction, world_corpus: true };
        }
        if (!guideSessionId) {
          pendingInteractions.push({ ...interaction, _topicTitle: title });
          dbg('guide-api: buffered (no session yet)', '#565f89');
          return;
        }
        void logInteraction(interaction)
          .then(id => {
            if (id) {
              lastInteractionId = id;
              if (title) topicToInteractionId.set(title, id);
              remoteLog(`guide-api: interaction ${id} (${title || '?'})`, 'guide-api');
              dbg(`#${id} ${title || '?'}`, '#C4A96A');
              setStatus('id', `#${id}`, 'ok');
            }
          })
          .catch(e => remoteLog(`guide-api: logInteraction failed: ${e}`, 'guide-api', 'Error'));
      };

      // Log each Guide response — main topic plus 2 additional_topics.
      // All three land as separate History rows so audio + image attach
      // per topic (instead of always sticking to the main one).
      bus.on('iacp:response', (res: any) => {
        if (!res?.content) return;
        const base = {
          session_id: guideSessionId || 0,
          city: lastCity, lat: lastLat, lon: lastLon,
          language: guide?.currentLanguage || pendingLang,
          theme: pendingMode,
        };
        // 1. Main topic
        logOne({
          ...base,
          topic_title: res.topic?.title || '',
          topic_wikipedia: res.topic?.wikipedia || '',
          content: res.content,
          vibe: res.vibe || '',
        }, res.topic?.title || '');
        // 2-3. Additional topics — each is its own interaction
        const additionals = Array.isArray(res.additional_topics) ? res.additional_topics : [];
        for (const at of additionals) {
          if (!at?.content) continue;
          logOne({
            ...base,
            topic_title: at.topic?.title || '',
            topic_wikipedia: at.topic?.wikipedia || '',
            // additional topics carry their own coordinates if known
            lat: at.topic?.lat ?? lastLat,
            lon: at.topic?.lon ?? lastLon,
            content: at.content,
            vibe: at.vibe || '',
          }, at.topic?.title || '');
        }
      });

      // Log song plays
      bus.on('music:started', (payload: any) => {
        const song = {
          session_id: guideSessionId || 0,
          query: payload?.query || '',
          title: payload?.title || '',
          storage_url: payload?.storageUrl || '',
        };
        if (guideSessionId) {
          void logSong(song)
            .then(() => remoteLog(`guide-api: song logged "${payload?.title}"`, 'guide-api'))
            .catch(e => remoteLog(`guide-api: logSong failed: ${e}`, 'guide-api', 'Error'));
        } else {
          pendingSongs.push(song);
        }
      });

      // TTS Audio archival.
      // We listen to BOTH events so we get the MP3 (from tts:end payload)
      // AND a guaranteed-correct title snapshot (from package:end, which
      // the lib emits BEFORE advancing the queue). Race-free pairing via
      // a small staged buffer.
      let pendingMp3: ArrayBuffer | null = null;
      let pendingEndedTitle: string | null = null;

      const tryArchive = async () => {
        if (!pendingMp3 || pendingEndedTitle === null) return;
        const mp3 = pendingMp3; pendingMp3 = null;
        const title = pendingEndedTitle; pendingEndedTitle = null;
        const intId = (title && topicToInteractionId.get(title)) || lastInteractionId;
        if (!intId) {
          remoteLog(`TTS archive skipped: no interaction ID for "${title}"`, 'audio', 'Warning');
          return;
        }
        const bytes = mp3.byteLength;
        dbg(`TTS archive: ${(bytes/1024).toFixed(0)}KB → interaction ${intId} (${title})`, '#565f89');
        remoteLog(`TTS archiving: ${(bytes/1024).toFixed(0)}KB for interaction ${intId} (${title})`, 'audio');
        try {
          const blob = new Blob([mp3], { type: 'audio/mpeg' });
          const formData = new FormData();
          formData.append('file', blob, `tts-${intId}.mp3`);
          formData.append('collection', 'wanderlaut-tts');
          const uploadRes = await fetch('https://api-storage.arkturian.com/storage/upload', {
            method: 'POST',
            headers: { 'X-API-KEY': 'Inetpass1' },
            body: formData,
          });
          if (!uploadRes.ok) {
            const errText = await uploadRes.text().catch(() => '');
            remoteLog(`TTS upload HTTP ${uploadRes.status}: ${errText.slice(0, 100)}`, 'audio', 'Error');
            return;
          }
          const data = await uploadRes.json();
          const storageId = data.id || data.asset_id;
          if (storageId) {
            await attachAudio(intId, storageId);
            remoteLog(`TTS archived: interaction=${intId} storage=${storageId} ${(bytes/1024).toFixed(0)}KB`, 'audio');
            dbg(`TTS archived: storage=${storageId}`, '#9ece6a');
          } else {
            remoteLog(`TTS upload returned no id: ${JSON.stringify(data).slice(0, 100)}`, 'audio', 'Error');
          }
        } catch (err) {
          remoteLog(`TTS upload failed: ${err instanceof Error ? err.message : err}`, 'audio', 'Error');
          dbg(`TTS archive error: ${err}`, '#f7768e');
        }
      };

      bus.on('package:end', (payload: any) => {
        pendingEndedTitle = payload?.title || '';
        void tryArchive();
      });

      bus.on('tts:end', (payload: any) => {
        if (!payload?.mp3) return;
        pendingMp3 = payload.mp3;
        void tryArchive();
      });
    }

    // Flush buffered interactions when guide-api session is ready
    bus.on('guide-api:session-ready', () => {
      remoteLog(`guide-api: session ready (id=${guideSessionId}), flushing ${pendingInteractions.length} interactions + ${pendingSongs.length} songs`, 'guide-api');
      flushPending();
    });

    // Auto-switch view on topic image
    let autoViewTimer: number | null = null;
    bus.on('topic:changed', (payload: { topic: any }) => {
      if (!payload.topic) return;
      // Only switch to fullscreen image for hero-sized photos (>800px)
      const hasImage = payload.topic.image && typeof payload.topic.image === 'string'
        && (!payload.topic.imageSize || payload.topic.imageSize === 'hero');
      if (hasImage && (document.body.getAttribute('data-view') || 'map') === 'map') {
        document.body.setAttribute('data-view', 'topic');
      }
      if (autoViewTimer !== null) window.clearTimeout(autoViewTimer);
      autoViewTimer = window.setTimeout(() => {
        if (document.body.getAttribute('data-view') === 'topic') {
          document.body.setAttribute('data-view', 'map');
        }
      }, 15000) as unknown as number;
    });
    bus.on('tts:end', () => {
      if (autoViewTimer !== null) window.clearTimeout(autoViewTimer);
      autoViewTimer = window.setTimeout(() => {
        if (document.body.getAttribute('data-view') === 'topic') {
          document.body.setAttribute('data-view', 'map');
        }
      }, 3000) as unknown as number;
    });

    // Dead session detection
    //
    // C2 (Iter1, 2026-06-05): timeout raised from 90s to 240s. The bot
    // now runs an explicit knowledge_near → species_get pipeline on
    // every flora_fauna GPS update, and Sonnet's high-effort thinking
    // adds 30-60s on top. The 90s budget was tripping reset/reload on
    // the FIRST response — perfectly healthy bots got killed mid-think.
    //
    // 240s is enough for the worst case observed during Iter1
    // verification (knowledge + species_get + composition + IACP-send
    // ≈ 180s with high-effort), with a 25% buffer.
    if (usingPool) {
      let firstSendTs = 0;
      let gotResponse = false;
      let watchdogHandle: number | null = null;
      bus.on('log:message', (entry: { text: string }) => {
        if (entry.text?.includes('→ Sent') && firstSendTs === 0) {
          firstSendTs = Date.now();
          watchdogHandle = window.setTimeout(() => {
            watchdogHandle = null;
            if (!gotResponse) {
              remoteLog(`Dead session: no response from ${session} after 240s`, 'pool', 'Error');
              // Phantom-Release-Prevention: remove beforeunload-handler
              // BEFORE the explicit release + reload below. Otherwise
              // the reload triggers a second releasePool() via the
              // beforeunload handler ~3s later, producing the
              // "acquired_by=unknown" Phantom-Release Pattern that
              // confuses pool-tracking (Cloud diagnosis 2026-06-08).
              if (onBeforeUnloadRelease) {
                window.removeEventListener('beforeunload', onBeforeUnloadRelease);
              }
              releasePool(session);
              localStorage.removeItem(USER_ID_KEY);
              updateStatus(t('status.sessionUnreachable'), t('status.sessionUnreachableDetail'));
              setTimeout(() => window.location.reload(), 2000);
            }
          }, 240000);
        }
      });
      bus.on('iacp:response', () => { gotResponse = true; });

      // Watchdog cleanup on explicit stop. Without this, the bare
      // setTimeout fires 240s after first-send EVEN IF the user stopped
      // seconds ago — confirmed by Debugger forensics 2026-06-08:
      // Stop@20s → watchdog@256s → reload@258s.
      // Belt + suspenders: clear the timer AND flip gotResponse so the
      // late callback would also no-op if the clear races.
      // Reset firstSendTs to 0 so the next start within the same page
      // can re-arm the watchdog (otherwise the second session never
      // gets its dead-session safety net — Debugger's second-bug catch).
      bus.on('user:stop', () => {
        if (watchdogHandle !== null) {
          window.clearTimeout(watchdogHandle);
          watchdogHandle = null;
        }
        gotResponse = true;
        firstSendTs = 0;
      });
    }

    // Long-press on map → context menu with "Position hier setzen"
    bus.on('map:contextmenu', (payload: { lat: number; lon: number; screenX: number; screenY: number }) => {
      // Remove any existing menu
      document.getElementById('mapContextMenu')?.remove();

      const menu = document.createElement('div');
      menu.id = 'mapContextMenu';
      const x = Math.min(Math.max(payload.screenX, 80), window.innerWidth - 180);
      const y = Math.min(Math.max(payload.screenY - 60, 60), window.innerHeight - 120);
      menu.style.cssText = `position:fixed;left:${x}px;top:${y}px;transform:translate(-50%,-100%);z-index:9999;background:rgba(14,14,20,0.96);border:1px solid rgba(196,169,106,0.3);border-radius:14px;padding:6px;backdrop-filter:blur(16px);box-shadow:0 8px 24px rgba(0,0,0,0.5);font-family:system-ui;display:flex;flex-direction:column;gap:4px;min-width:160px;animation:fadeIn 0.15s ease`;

      const setBtn = document.createElement('button');
      setBtn.textContent = t('toast.setPosHere');
      setBtn.style.cssText = 'background:rgba(196,169,106,0.15);border:none;color:#C4A96A;padding:10px 14px;border-radius:10px;font-size:13px;font-weight:600;cursor:pointer;text-align:left;font-family:inherit';
      setBtn.addEventListener('click', () => {
        g.setManualLocation({ lat: payload.lat, lon: payload.lon, accuracy: 5, speed: 0, heading: null, timestamp: Date.now() });
        menu.remove();
      });

      const cancelBtn = document.createElement('button');
      cancelBtn.textContent = t('common.cancel');
      cancelBtn.style.cssText = 'background:none;border:none;color:rgba(247,245,240,0.4);padding:8px 14px;border-radius:10px;font-size:12px;cursor:pointer;text-align:left;font-family:inherit';
      cancelBtn.addEventListener('click', () => menu.remove());

      menu.appendChild(setBtn);
      menu.appendChild(cancelBtn);
      document.body.appendChild(menu);

      // Close on outside tap (after a small delay so the long-press doesn't immediately close it)
      setTimeout(() => {
        const closeOnOutside = (e: MouseEvent | TouchEvent) => {
          if (!menu.contains(e.target as Node)) {
            menu.remove();
            document.removeEventListener('click', closeOnOutside);
            document.removeEventListener('touchstart', closeOnOutside);
          }
        };
        document.addEventListener('click', closeOnOutside);
        document.addEventListener('touchstart', closeOnOutside);
      }, 150);
    });

    // Tab resume detection — diagnostic only. We used to auto-reload
    // after 2min hidden, but that triggered a fresh geolocation
    // permission prompt on Firefox (which doesn't carry "Beim Besuch
    // der Website erlauben" across page reloads) and threw away the
    // background music + active TTS pipeline that the user actually
    // wanted to keep. Tab-resume is now silent; if a pool/SSE
    // connection died, the next user action will surface that error.
    let lastVisibleAt = Date.now();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        lastVisibleAt = Date.now();
      } else if (document.visibilityState === 'visible') {
        const hiddenMs = Date.now() - lastVisibleAt;
        if (hiddenMs > 30000) {
          remoteLog(`Tab resumed after ${Math.round(hiddenMs / 1000)}s`, 'lifecycle');
        }
      }
    });

    // Skin switch
    document.getElementById('skinSwitchBtn')?.addEventListener('click', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('skin', skinParam === 'admin' ? '' : 'admin');
      window.location.href = url.toString();
    });

    (window as unknown as { __guide: AudioGuide }).__guide = g;
  }

  // ── Restore from saved state ──
  if (sessionState.canResume && isLoggedIn()) {
    remoteLog(`Restoring: ${sessionState.state.appState}`, 'restore');
    // Re-apply the user's "Konkreter Ort" pick if there was one — this
    // is the auto-resume path after a dead-session 240s reload, the
    // user expects to wake up where they were, not at live-GPS.
    if (storedPrefs.customLocation) {
      remoteLog(`Restoring customLocation="${storedPrefs.customLocation}"`, 'restore');
      void (async () => {
        const resolved = await resolveOverridePosition(storedPrefs.customLocation!);
        if (resolved) {
          (window as any).__guideOverridePosition = {
            lat: resolved.lat, lon: resolved.lon, label: resolved.label,
          };
        }
        void startTour();
      })();
    } else {
      void startTour();
    }
  }
}

bootstrap().catch((err: unknown) => {
  const msg = err instanceof Error ? err.message : String(err);
  const stack = err instanceof Error && err.stack ? err.stack : '';
  console.error('Boot failed:', err);
  remoteLog(`BOOT FAILED: ${msg}`, 'boot', 'Error');
  document.body.innerHTML = `<pre style="color:#f7768e;background:#0a0a0f;padding:2rem;font-family:monospace;min-height:100vh;margin:0;white-space:pre-wrap">BOOT FAILED: ${msg}\n\n${stack}</pre>`;
});
